import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Button, Form, Modal, Table, InputGroup, ButtonGroup, Spinner } from 'react-bootstrap';
import {
  FaPlus, FaTrash, FaEdit, FaDownload, FaKey, FaLink,
  FaDatabase, FaSearchPlus, FaSearchMinus, FaRedo,
  FaColumns, FaCopy, FaCheck, FaTimes, FaProjectDiagram,
  FaBars, FaSave, FaFolderOpen, FaHistory, FaStickyNote,
  FaInfoCircle, FaExclamationTriangle
} from 'react-icons/fa';
import '../css/DevSkeleton.css';
import axiosInstance from '../api/axiosInstance';
import useAuthStore from '../stores/authStore';
import { SAMPLE_BASIC, SAMPLE_ECOMMERCE, SAMPLE_LMS } from './sampleData';

// 1. DBMS 대푯값 정의
const DBMS_OPTIONS = ['PostgreSQL', 'Oracle', 'MySQL', 'SQL Server', 'SQLite'];

// 2. DBMS별 기본 추천 데이터 타입 목록
const RECOMMENDED_TYPES = {
  PostgreSQL: ['SERIAL', 'BIGSERIAL', 'VARCHAR', 'TEXT', 'INTEGER', 'BIGINT', 'NUMERIC', 'BOOLEAN', 'TIMESTAMP', 'DATE', 'BYTEA', 'JSONB', 'JSON'],
  Oracle: ['NUMBER(10) GENERATED AS IDENTITY', 'NUMBER', 'VARCHAR2', 'CLOB', 'DATE', 'TIMESTAMP', 'BLOB'],
  MySQL: ['INT AUTO_INCREMENT', 'BIGINT AUTO_INCREMENT', 'VARCHAR', 'TEXT', 'INT', 'BIGINT', 'DECIMAL', 'TINYINT(1)', 'DATETIME', 'LONGBLOB', 'JSON'],
  'SQL Server': ['INT IDENTITY(1,1)', 'BIGINT IDENTITY(1,1)', 'VARCHAR', 'VARCHAR(MAX)', 'INT', 'BIGINT', 'DECIMAL', 'BIT', 'DATETIME2', 'VARBINARY(MAX)'],
  SQLite: ['INTEGER PRIMARY KEY AUTOINCREMENT', 'TEXT', 'INTEGER', 'REAL', 'BLOB']
};

// 3. DBMS 간 데이터 타입 변환 매핑 함수
const convertDataType = (type, toDb) => {
  if (!type) return '';
  const t = type.toUpperCase().trim();

  let category = 'VARCHAR';
  if (t.includes('SERIAL') || t.includes('IDENTITY') || t.includes('AUTO_INCREMENT') || (t.includes('INTEGER') && t.includes('AUTOINCREMENT'))) {
    category = 'SERIAL';
  } else if (t.includes('INT') || t.includes('INTEGER') || t.includes('NUMBER(10)') || t.includes('NUMBER(19)')) {
    category = 'INT';
  } else if (t.includes('CHAR') || t.includes('VARCHAR') || t.includes('VARCHAR2')) {
    category = 'VARCHAR';
  } else if (t.includes('TEXT') || t.includes('CLOB')) {
    category = 'TEXT';
  } else if (t.includes('DECIMAL') || t.includes('NUMERIC') || t.includes('NUMBER') || t.includes('REAL') || t.includes('DOUBLE')) {
    category = 'DECIMAL';
  } else if (t.includes('DATE') || t.includes('TIME') || t.includes('TIMESTAMP')) {
    category = 'DATETIME';
  } else if (t.includes('BOOL') || t.includes('TINYINT(1)') || t.includes('BIT')) {
    category = 'BOOLEAN';
  } else if (t.includes('BLOB') || t.includes('BYTEA') || t.includes('BINARY') || t.includes('VARBINARY')) {
    category = 'BLOB';
  } else if (t.includes('JSONB') || t.includes('JSON')) {
    category = 'JSON';
  }

  const mappings = {
    PostgreSQL: {
      SERIAL: 'SERIAL',
      INT: 'INTEGER',
      VARCHAR: 'VARCHAR',
      TEXT: 'TEXT',
      DECIMAL: 'NUMERIC',
      DATETIME: 'TIMESTAMP',
      BOOLEAN: 'BOOLEAN',
      BLOB: 'BYTEA',
      JSON: 'JSONB'
    },
    Oracle: {
      SERIAL: 'NUMBER(10) GENERATED AS IDENTITY',
      INT: 'NUMBER(10)',
      VARCHAR: 'VARCHAR2',
      TEXT: 'CLOB',
      DECIMAL: 'NUMBER',
      DATETIME: 'TIMESTAMP',
      BOOLEAN: 'NUMBER(1)',
      BLOB: 'BLOB',
      JSON: 'CLOB'
    },
    MySQL: {
      SERIAL: 'INT AUTO_INCREMENT',
      INT: 'INT',
      VARCHAR: 'VARCHAR',
      TEXT: 'TEXT',
      DECIMAL: 'DECIMAL',
      DATETIME: 'DATETIME',
      BOOLEAN: 'TINYINT(1)',
      BLOB: 'LONGBLOB',
      JSON: 'JSON'
    },
    'SQL Server': {
      SERIAL: 'INT IDENTITY(1,1)',
      INT: 'INT',
      VARCHAR: 'VARCHAR',
      TEXT: 'VARCHAR(MAX)',
      DECIMAL: 'DECIMAL',
      DATETIME: 'DATETIME2',
      BOOLEAN: 'BIT',
      BLOB: 'VARBINARY(MAX)',
      JSON: 'VARCHAR(MAX)'
    },
    SQLite: {
      SERIAL: 'INTEGER PRIMARY KEY AUTOINCREMENT',
      INT: 'INTEGER',
      VARCHAR: 'TEXT',
      TEXT: 'TEXT',
      DECIMAL: 'REAL',
      DATETIME: 'TEXT',
      BOOLEAN: 'INTEGER',
      BLOB: 'BLOB',
      JSON: 'TEXT'
    }
  };

  return mappings[toDb]?.[category] || type;
};

// 3.1 특정 데이터 타입이 길이를 지원하는지 판별
const doesTypeSupportSize = (type) => {
  if (!type) return false;
  const t = type.toUpperCase().trim();
  if (t.includes('(')) return false; // 이미 괄호가 포함되어 있다면(예: NUMBER(10) 등) 추가 길이 미지원
  
  return (
    t.startsWith('VARCHAR') ||
    t.startsWith('CHAR') ||
    t.startsWith('NVARCHAR') ||
    t.startsWith('CHARACTER') ||
    t.startsWith('DECIMAL') ||
    t.startsWith('NUMERIC') ||
    t.startsWith('NUMBER') ||
    t.startsWith('VARBINARY') ||
    t.startsWith('BINARY')
  );
};

// 3.2 테이블 및 관계선 유실/오류 정보 자가 치유 헬퍼
const healTablesAndRelationships = (tablesList, relsList) => {
  let updatedRels = [...relsList];
  
  const updatedTables = tablesList.map(table => {
    const updatedCols = table.columns.map(col => {
      if (col.fk) {
        // 1. 관계선 배열 내에 이 컬럼을 가리키는 라인이 존재하는지 확인
        const hasLine = updatedRels.some(r => r.childTableId === table.id && r.childColumnId === col.id);
        if (hasLine) return col;
        
        // 2. 라인이 없는데 fkRef가 유효한 경우 라인 복원
        if (col.fkRef && col.fkRef.tableId && col.fkRef.columnId) {
          const parentTable = tablesList.find(t => t.id === col.fkRef.tableId);
          const parentCol = parentTable?.columns.find(c => c.id === col.fkRef.columnId);
          if (parentTable && parentCol) {
            updatedRels.push({
              id: 'r_healed_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4),
              parentTableId: col.fkRef.tableId,
              parentColumnId: col.fkRef.columnId,
              childTableId: table.id,
              childColumnId: col.id,
              type: '1:N'
            });
            return col;
          }
        }
        
        // 3. fkRef가 유실되었으나 타 테이블에 동일한 이름의 PK가 존재하는 경우 복구 매핑
        const childPhysName = col.physicalName.toLowerCase();
        let foundParent = null;
        let foundParentCol = null;
        
        for (const t of tablesList) {
          if (t.id === table.id) continue;
          const pkCol = t.columns.find(c => c.pk && c.physicalName.toLowerCase() === childPhysName);
          if (pkCol) {
            foundParent = t;
            foundParentCol = pkCol;
            break;
          }
        }
        
        if (foundParent && foundParentCol) {
          updatedRels.push({
            id: 'r_healed_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4),
            parentTableId: foundParent.id,
            parentColumnId: foundParentCol.id,
            childTableId: table.id,
            childColumnId: col.id,
            type: '1:N'
          });
          return {
            ...col,
            fkRef: {
              tableId: foundParent.id,
              columnId: foundParentCol.id
            }
          };
        }
        
        // 4. 어떤 매칭도 이루어지지 않는 경우 일반 컬럼(fk: false)으로 복구 치유
        return {
          ...col,
          fk: false,
          fkRef: null
        };
      }
      return col;
    });
    
    return {
      ...table,
      columns: updatedCols
    };
  });
  
  return {
    tables: updatedTables,
    relationships: updatedRels
  };
};

// 4. 초기 견본 데이터 정의
const INITIAL_TABLES = [
  {
    id: 't_user',
    physicalName: 'tb_user',
    logicalName: '사용자 정보',
    comment: '서비스 회원 정보를 관리하는 테이블',
    x: 100,
    y: 120,
    columns: [
      { id: 'c_user_no', physicalName: 'user_no', logicalName: '사용자번호', dataType: 'SERIAL', size: '', pk: true, fk: false, notNull: true, unique: true, defaultValue: '', comment: '사용자 일련번호' },
      { id: 'c_user_id', physicalName: 'user_id', logicalName: '아이디', dataType: 'VARCHAR', size: '50', pk: false, fk: false, notNull: true, unique: true, defaultValue: '', comment: '로그인용 계정 ID' },
      { id: 'c_user_name', physicalName: 'user_name', logicalName: '이름', dataType: 'VARCHAR', size: '100', pk: false, fk: false, notNull: false, unique: false, defaultValue: '', comment: '사용자 실명' },
      { id: 'c_created_at', physicalName: 'created_at', logicalName: '가입일시', dataType: 'TIMESTAMP', size: '', pk: false, fk: false, notNull: true, unique: false, defaultValue: 'CURRENT_TIMESTAMP', comment: '회원 가입 일자' }
    ]
  },
  {
    id: 't_post',
    physicalName: 'tb_post',
    logicalName: '게시글 정보',
    comment: '자유게시판에 등록된 게시글 테이블',
    x: 520,
    y: 180,
    columns: [
      { id: 'c_post_no', physicalName: 'post_no', logicalName: '게시글번호', dataType: 'SERIAL', size: '', pk: true, fk: false, notNull: true, unique: true, defaultValue: '', comment: '게시글 일련번호' },
      { id: 'c_ref_user_no', physicalName: 'user_no', logicalName: '작성자번호', dataType: 'INTEGER', size: '', pk: false, fk: true, notNull: true, unique: false, defaultValue: '', comment: 'tb_user 테이블의 user_no 참조', fkRef: { tableId: 't_user', columnId: 'c_user_no' } },
      { id: 'c_title', physicalName: 'title', logicalName: '제목', dataType: 'VARCHAR', size: '200', pk: false, fk: false, notNull: true, unique: false, defaultValue: '', comment: '게시물 제목' },
      { id: 'c_content', physicalName: 'content', logicalName: '내용', dataType: 'TEXT', size: '', pk: false, fk: false, notNull: false, unique: false, defaultValue: '', comment: '본문 내용' },
      { id: 'c_post_created_at', physicalName: 'created_at', logicalName: '작성일시', dataType: 'TIMESTAMP', size: '', pk: false, fk: false, notNull: true, unique: false, defaultValue: 'CURRENT_TIMESTAMP', comment: '작성 일자' }
    ]
  }
];

const INITIAL_RELATIONSHIPS = [
  {
    id: 'r_user_post',
    parentTableId: 't_user',
    parentColumnId: 'c_user_no',
    childTableId: 't_post',
    childColumnId: 'c_ref_user_no',
    type: '1:N'
  }
];

// --- 간단한 마크다운 파서 ---
const parseMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
  // Bold
  html = html.replace(/\*\*(.*)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*(.*)\*/g, '<em>$1</em>');
  // List items
  html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
  // Line breaks
  html = html.replace(/\n/g, '<br />');
  return html;
};

export function DevSkeleton() {
  // --- States ---
  const [tables, setTables] = useState(() => {
    const saved = localStorage.getItem('wajok_erd_tables');
    return saved ? JSON.parse(saved) : INITIAL_TABLES;
  });
  const [relationships, setRelationships] = useState(() => {
    const saved = localStorage.getItem('wajok_erd_relations');
    return saved ? JSON.parse(saved) : INITIAL_RELATIONSHIPS;
  });
  const [selectedDb, setSelectedDb] = useState(() => {
    const saved = localStorage.getItem('wajok_erd_db');
    return saved || 'PostgreSQL';
  });
  const [viewMode, setViewMode] = useState('both'); // 'physical' | 'logical' | 'both'
  const [canvasScale, setCanvasScale] = useState(1);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);

  // --- Custom Alert Modal State ---
  const [alertModal, setAlertModal] = useState({
    show: false,
    title: '알림',
    message: '',
    variant: 'info' // 'success' | 'danger' | 'info' | 'warning'
  });

  const triggerAlert = useCallback((message, title = '알림', variant = 'info') => {
    setAlertModal({
      show: true,
      title,
      message,
      variant
    });
  }, []);

  // --- Custom Confirm Modal State ---
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '확인',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  const triggerConfirm = useCallback((message, onConfirm, title = '확인', onCancel = null) => {
    setConfirmModal({
      show: true,
      title,
      message,
      onConfirm: () => {
        if (onConfirm) onConfirm();
        setConfirmModal(prev => ({ ...prev, show: false }));
      },
      onCancel: () => {
        if (onCancel) onCancel();
        setConfirmModal(prev => ({ ...prev, show: false }));
      }
    });
  }, []);

  // --- Auth Store & Cloud Sync States ---
  const { isAuthenticated: isLoggedIn, user } = useAuthStore();
  const [projectId, setProjectId] = useState(() => {
    const saved = localStorage.getItem('wajok_erd_project_id');
    return saved ? parseInt(saved) : null;
  });
  const [projectTitle, setProjectTitle] = useState(() => {
    return localStorage.getItem('wajok_erd_project_title') || '';
  });
  const [projectDescription, setProjectDescription] = useState(() => {
    return localStorage.getItem('wajok_erd_project_desc') || '';
  });
  const [currentVersionNo, setCurrentVersionNo] = useState(() => {
    const saved = localStorage.getItem('wajok_erd_version_no');
    return saved ? parseInt(saved) : null;
  });

  useEffect(() => {
    console.log("DevSkeleton Auth Check:", { isLoggedIn, user });
  }, [isLoggedIn, user]);

  const prevUserIdRef = useRef(user?.id);

  useEffect(() => {
    if (prevUserIdRef.current !== user?.id) {
      // Clear localStorage diagram state
      localStorage.removeItem('wajok_erd_tables');
      localStorage.removeItem('wajok_erd_relations');
      localStorage.removeItem('wajok_erd_db');
      localStorage.removeItem('wajok_erd_sticky_notes');
      localStorage.removeItem('wajok_erd_project_id');
      localStorage.removeItem('wajok_erd_project_title');
      localStorage.removeItem('wajok_erd_project_desc');
      localStorage.removeItem('wajok_erd_version_no');

      // Reset workspace states
      setTables(INITIAL_TABLES);
      setRelationships(INITIAL_RELATIONSHIPS);
      setSelectedDb('PostgreSQL');
      setStickyNotes([]);
      setProjectId(null);
      setProjectTitle('');
      setProjectDescription('');
      setCurrentVersionNo(null);
      setCanvasPan({ x: 0, y: 0 });
      setCanvasScale(1);
      setSelectedTableId(null);
      setSelectedTableIds([]);
      setSelectedNoteIds([]);

      prevUserIdRef.current = user?.id;
    }
  }, [user?.id]);

  // --- 최초 로드 시 다이어그램 자가 치유 및 복원 (유실 관계선 복구 & 유령 FK 치유) ---
  useEffect(() => {
    if (tables && tables.length > 0) {
      const healed = healTablesAndRelationships(tables, relationships);
      const tablesStr = JSON.stringify(tables);
      const healedTablesStr = JSON.stringify(healed.tables);
      const relsStr = JSON.stringify(relationships);
      const healedRelsStr = JSON.stringify(healed.relationships);
      
      if (tablesStr !== healedTablesStr || relsStr !== healedRelsStr) {
        setTables(healed.tables);
        setRelationships(healed.relationships);
        console.log("Self-healing Engine: Auto-restored missing relationship lines or cleaned ghost FKs.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cloud Modals Control
  const [showCloudSaveModal, setShowCloudSaveModal] = useState(false);
  const [showCloudLoadModal, setShowCloudLoadModal] = useState(false);
  const [showCloudHistoryModal, setShowCloudHistoryModal] = useState(false);

  // Cloud Save inputs
  const [saveTitle, setSaveTitle] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [saveCommitMsg, setSaveCommitMsg] = useState('');

  // Cloud Lists & Loading states
  const [cloudProjects, setCloudProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // 테이블 검색 쿼리 상태
  const [tableSearchQuery, setTableSearchQuery] = useState('');

  // 특정 테이블 포커스 하이라이트 상태
  const [highlightedTableId, setHighlightedTableId] = useState(null);

  // AI 대화 세션 ID 및 상태 관리
  const [aiSessionId] = useState(() => {
    let saved = localStorage.getItem('wajok_erd_ai_session_id');
    if (!saved) {
      saved = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
      localStorage.setItem('wajok_erd_ai_session_id', saved);
    }
    return saved;
  });
  const [aiMessages, setAiMessages] = useState([
    { type: 'assistant', text: '안녕하세요! 설계하고 싶은 데이터베이스 구조나 기능 요구사항을 프롬프트로 입력해주세요. AI가 적절한 테이블 구조를 매핑해 줍니다.' }
  ]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [processingTime, setProcessingTime] = useState(0); // AI 응답 대기 경과 시간 (초)
  const [proposedData, setProposedData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // --- Sticky Notes States ---
  const [stickyNotes, setStickyNotes] = useState(() => {
    const saved = localStorage.getItem('wajok_erd_sticky_notes');
    return saved ? JSON.parse(saved) : [];
  });
  const [editingNoteId, setEditingNoteId] = useState(null);

  // DDL Drawer State
  const [isDdlExpanded, setIsDdlExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);

  // Column drag state
  const [draggedColInfo, setDraggedColInfo] = useState(null); // { tableId, colId, index }

  // Modals
  const [showTableModal, setShowTableModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);

  // Mouse Drag Tracking (for Canvas panning, Table moving, Connection dragging)
  const canvasContainerRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const [dragState, setDragState] = useState(null); // { type: 'table'|'pan'|'connect', data: ... }

  // --- LocalStorage 동기화 ---
  useEffect(() => {
    localStorage.setItem('wajok_erd_tables', JSON.stringify(tables));
  }, [tables]);

  useEffect(() => {
    localStorage.setItem('wajok_erd_relations', JSON.stringify(relationships));
  }, [relationships]);

  useEffect(() => {
    localStorage.setItem('wajok_erd_db', selectedDb);
  }, [selectedDb]);

  useEffect(() => {
    localStorage.setItem('wajok_erd_sticky_notes', JSON.stringify(stickyNotes));
  }, [stickyNotes]);

  useEffect(() => {
    if (projectId) {
      localStorage.setItem('wajok_erd_project_id', projectId.toString());
    } else {
      localStorage.removeItem('wajok_erd_project_id');
    }
  }, [projectId]);

  useEffect(() => {
    localStorage.setItem('wajok_erd_project_title', projectTitle);
  }, [projectTitle]);

  useEffect(() => {
    localStorage.setItem('wajok_erd_project_desc', projectDescription);
  }, [projectDescription]);

  useEffect(() => {
    if (currentVersionNo) {
      localStorage.setItem('wajok_erd_version_no', currentVersionNo.toString());
    } else {
      localStorage.removeItem('wajok_erd_version_no');
    }
  }, [currentVersionNo]);

  // --- DBMS 전환 시 자동 데이터타입 매핑 ---
  const handleDbChange = (e) => {
    const targetDb = e.target.value;
    const previousDb = selectedDb;
    setSelectedDb(targetDb);

    // 컬럼들의 데이터타입을 신규 DBMS에 맞추어 변환
    setTables(prevTables => {
      return prevTables.map(table => ({
        ...table,
        columns: table.columns.map(col => {
          // PK 및 Auto-Increment 복합 처리
          let newType = convertDataType(col.dataType, targetDb);

          // SQLite의 자동증가 PK에 대한 결합형 처리
          if (targetDb === 'SQLite' && col.pk && (col.dataType.includes('SERIAL') || col.dataType.includes('AUTO_INCREMENT') || col.dataType.includes('IDENTITY'))) {
            newType = 'INTEGER PRIMARY KEY AUTOINCREMENT';
          } else if (previousDb === 'SQLite' && col.dataType.toUpperCase().includes('AUTOINCREMENT')) {
            // SQLite에서 다른 DBMS로 넘어갈 시 적절한 시리얼 타입 지정
            newType = convertDataType('SERIAL', targetDb);
          }

          return {
            ...col,
            dataType: newType
          };
        })
      }));
    });
  };

  // --- 줌 & 팬 컨트롤 ---
  const handleZoom = (factor) => {
    setCanvasScale(prev => Math.min(1.5, Math.max(0.5, prev + factor)));
  };
  const resetZoom = () => {
    setCanvasScale(1);
    setCanvasPan({ x: 0, y: 0 });
  };

  // AI 채팅 메시지 자동 스크롤
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [aiMessages, aiLoading]);

  // AI 응답 대기 타이머
  useEffect(() => {
    let interval;
    if (aiLoading) {
      setProcessingTime(0);
      const startTime = Date.now();
      interval = setInterval(() => {
        setProcessingTime((Date.now() - startTime) / 1000);
      }, 100);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [aiLoading]);

  // AI 메세지 전송 (n8n Webhook 연동)
  const handleSendAiMessage = async (e) => {
    if (e) e.preventDefault();
    if (!aiInput.trim()) return;

    const userMsg = aiInput.trim();
    setAiMessages(prev => [...prev, { type: 'user', text: userMsg }]);
    setAiInput('');
    setAiLoading(true);

    try {
      // POST https://n8n.wajok.com/webhook/api/erd 호출 (timeout 1시간)
      const response = await axiosInstance.post('erd', {
        reqType: 'aigen',
        session_id: aiSessionId,
        message: userMsg
      }, {
        timeout: 3600000 // 1시간 (3,600,000 ms)
      });

      const resData = response.data;
      const output = resData?.output || resData;

      if (output && Array.isArray(output.tables)) {
        const cleanSize = (sz) => {
          if (sz === undefined || sz === null) return '';
          const s = sz.toString().trim();
          if (s === "''" || s === '""' || s === '""' || s === "''" || s === '') return '';
          return s.replace(/['"]/g, '').trim();
        };

        output.tables = output.tables.map(table => ({
          ...table,
          columns: (table.columns || []).map(col => {
            let cleanDef = col.defaultValue ? col.defaultValue.toString().trim() : '';
            if (cleanDef) {
              cleanDef = cleanDef.replace(/^['"]|['"]$/g, '');
            }
            return {
              ...col,
              size: cleanSize(col.size),
              defaultValue: cleanDef
            };
          })
        }));

        setAiMessages(prev => [
          ...prev,
          { type: 'assistant', text: '데이터베이스 설계안이 완성되었습니다! 제안된 설계 구조를 확인하시려면 하단 또는 화면의 팝업 창을 이용해 주세요.' }
        ]);
        setProposedData(output);
        setShowPreviewModal(true);
      } else {
        throw new Error('올바르지 않은 응답 규격입니다.');
      }
    } catch (err) {
      console.error(err);
      setAiMessages(prev => [
        ...prev,
        { type: 'assistant', text: '죄송합니다. AI 모델로부터 설계 구조를 파싱하지 못했거나 네트워크 타임아웃(1시간)이 경과했습니다.' }
      ]);
      triggerAlert(
        'AI 설계 응답 수신에 실패했습니다. 서버 상태 또는 네트워크를 확인해 주세요.',
        'AI 설계 분석 실패',
        'danger'
      );
    } finally {
      setAiLoading(false);
    }
  };

  // 제안받은 AI 설계 데이터 반영 가공 및 적용
  const handleApplyProposedData = (actionOverride) => {
    if (!proposedData) return;

    const action = actionOverride || proposedData.action || 'ADD';

    const performApply = () => {
      const physicalToTableId = {};
      const colPhysicalToId = {}; // key: "tableName_colName"

      // 기존 테이블 정보 수집 (기존 테이블 정보 맵에 기재하여 신규 관계선 이을 때 병합 참조 가능케 함)
      if (action === 'ADD') {
        tables.forEach(t => {
          physicalToTableId[t.physicalName] = t.id;
          t.columns.forEach(c => {
            colPhysicalToId[`${t.physicalName}_${c.physicalName}`] = c.id;
          });
        });
      }

      // 배치 좌표 산출
      let startX = 100;
      let startY = 100;
      if (action === 'ADD' && tables.length > 0) {
        startX = Math.max(...tables.map(t => t.x)) + 300;
        if (startX > 2000) {
          startX = 100;
          startY = Math.max(...tables.map(t => t.y)) + 300;
        }
      }

      const gridCols = 3;
      const xSpacing = 300;
      const ySpacing = 280;

      // 신규 테이블 & 컬럼 가공
      const newTables = (proposedData.tables || []).map((t, idx) => {
        const tableId = 't_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4);
        physicalToTableId[t.physicalName] = tableId;

        const columns = (t.columns || []).map(c => {
          const colId = 'c_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4);
          colPhysicalToId[`${t.physicalName}_${c.physicalName}`] = colId;

          let cleanDef = c.defaultValue || '';
          if (cleanDef) {
            cleanDef = cleanDef.toString().trim().replace(/^['"]|['"]$/g, '');
          }

          return {
            id: colId,
            physicalName: c.physicalName,
            logicalName: c.logicalName || '',
            dataType: c.dataType || 'VARCHAR',
            size: c.size || '',
            pk: !!c.pk,
            fk: !!c.fk,
            notNull: !!c.notNull,
            unique: !!c.unique,
            defaultValue: cleanDef,
            comment: c.comment || ''
          };
        });

        const x = startX + (idx % gridCols) * xSpacing;
        const y = startY + Math.floor(idx / gridCols) * ySpacing;

        return {
          id: tableId,
          physicalName: t.physicalName,
          logicalName: t.logicalName || '',
          comment: t.comment || '',
          x,
          y,
          columns
        };
      });

      // 신규 관계 가공 및 번역
      const newRelationships = [];
      (proposedData.relationships || []).forEach(r => {
        const pTableId = physicalToTableId[r.parentTable];
        const cTableId = physicalToTableId[r.childTable];
        const pColId = colPhysicalToId[`${r.parentTable}_${r.parentColumn}`];
        const cColId = colPhysicalToId[`${r.childTable}_${r.childColumn}`];

        if (pTableId && cTableId && pColId && cColId) {
          newRelationships.push({
            id: 'r_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4),
            parentTableId: pTableId,
            parentColumnId: pColId,
            childTableId: cTableId,
            childColumnId: cColId,
            type: r.type || '1:N'
          });
        }
      });

      const combinedTables = action === 'REPLACE' ? newTables : [...tables, ...newTables];
      const combinedRels = action === 'REPLACE' ? newRelationships : [...relationships, ...newRelationships];
      
      const healed = healTablesAndRelationships(combinedTables, combinedRels);

      if (action === 'REPLACE') {
        setTables(healed.tables);
        setRelationships(healed.relationships);
        triggerAlert('AI 제안 스키마가 성공적으로 반영되었습니다. 기존 설계가 대체되었습니다.', '설계 대체 완료', 'success');
      } else {
        setTables(healed.tables);
        setRelationships(healed.relationships);
        triggerAlert('AI 제안 스키마가 현재 설계에 추가 반영되었습니다.', '설계 추가 완료', 'success');
      }

      setShowPreviewModal(false);
      setProposedData(null);
    };

    if (action === 'REPLACE') {
      triggerConfirm(
        '기존의 모든 테이블과 관계선을 지우고 AI가 제안한 스키마로 완전히 대체하시겠습니까?',
        performApply,
        '완전 대체 확인'
      );
    } else {
      performApply();
    }
  };

  // --- 테이블 생성 및 삭제 ---
  const handleAddTable = () => {
    const newId = 't_' + Date.now();
    const newTable = {
      id: newId,
      physicalName: `tb_table_${tables.length + 1}`,
      logicalName: `새테이블_${tables.length + 1}`,
      comment: '',
      x: Math.max(50, -canvasPan.x + 100),
      y: Math.max(50, -canvasPan.y + 100),
      columns: [
        {
          id: 'c_' + Date.now() + '_1',
          physicalName: 'id',
          logicalName: '일련번호',
          dataType: selectedDb === 'SQLite' ? 'INTEGER PRIMARY KEY AUTOINCREMENT' : convertDataType('SERIAL', selectedDb),
          size: '',
          pk: true,
          fk: false,
          notNull: true,
          unique: true,
          defaultValue: '',
          comment: '기본 키 일련번호'
        }
      ]
    };
    setTables([...tables, newTable]);
    setSelectedTableId(newId);
  };

  const handleDeleteTable = (tableId, e) => {
    if (e) e.stopPropagation();
    triggerConfirm(
      '이 테이블과 연결된 모든 관계선이 함께 삭제됩니다. 정말 삭제하시겠습니까?',
      () => {
        setTables(prevTables => prevTables.filter(t => t.id !== tableId));
        setRelationships(prevRels => prevRels.filter(r => r.parentTableId !== tableId && r.childTableId !== tableId));

        // 외래키 리퍼런스도 해제
        setTables(prev => prev.map(t => ({
          ...t,
          columns: t.columns.map(c => {
            if (c.fkRef && c.fkRef.tableId === tableId) {
              const { fkRef, ...rest } = c;
              return { ...rest, fk: false };
            }
            return c;
          })
        })));

        if (selectedTableId === tableId) setSelectedTableId(null);
      },
      '테이블 삭제'
    );
  };

  // --- 테이블 이동 마우스 이벤트 ---
  const handleTableHeaderMouseDown = (tableId, e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    const isSelected = selectedTableIds.includes(tableId);
    let nextTableIds = selectedTableIds;
    let nextNoteIds = selectedNoteIds;

    if (!isSelected) {
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        nextTableIds = [...selectedTableIds, tableId];
      } else {
        nextTableIds = [tableId];
        nextNoteIds = [];
      }
      setSelectedTableIds(nextTableIds);
      setSelectedNoteIds(nextNoteIds);
      setSelectedTableId(tableId);
    } else {
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        nextTableIds = selectedTableIds.filter(id => id !== tableId);
        setSelectedTableIds(nextTableIds);
        setSelectedTableId(nextTableIds[0] || null);
      }
    }

    const initTables = tables.map(t => ({ id: t.id, x: t.x, y: t.y }));
    const initNotes = stickyNotes.map(n => ({ id: n.id, x: n.x, y: n.y }));

    setDragState({
      type: 'multi-drag',
      startX: e.clientX,
      startY: e.clientY,
      initTables,
      initNotes,
      draggedTableId: tableId,
      clickOnly: isSelected && !(e.ctrlKey || e.metaKey || e.shiftKey)
    });
  };

  // --- 관계 맺기 드래그 마우스 이벤트 ---
  const handleConnectorMouseDown = (tableId, columnId, e) => {
    e.stopPropagation();
    e.preventDefault();
    const table = tables.find(t => t.id === tableId);
    const colIndex = table.columns.findIndex(c => c.id === columnId);

    // 시작 좌표 계산 (정밀하게 anchor 포인트 중심값 계산)
    const cardWidth = 260;
    const headerHeight = 52;
    const rowHeight = 30;
    const startX = table.x + cardWidth;
    const startY = table.y + headerHeight + (colIndex * rowHeight) + (rowHeight / 2);

    setDragState({
      type: 'connect',
      parentTableId: tableId,
      parentColumnId: columnId,
      startX,
      startY,
      curX: startX,
      curY: startY
    });
  };

  // --- 스티커 메모 추가 및 편집 핸들러 ---
  const handleAddStickyNote = () => {
    const newNote = {
      id: 'note_' + Date.now(),
      x: 150 + Math.random() * 50,
      y: 150 + Math.random() * 50,
      w: 240,
      h: 160,
      color: '#fef08a', // 기본 노란색
      content: '### 메모 제목\n- 내용을 입력하세요.\n- 마크다운 작성을 지원합니다.'
    };
    setStickyNotes(prev => [...prev, newNote]);
  };

  const handleUpdateNoteContent = (noteId, val) => {
    setStickyNotes(prev => prev.map(n => n.id === noteId ? { ...n, content: val } : n));
  };

  const handleUpdateNoteColor = (noteId, color) => {
    setStickyNotes(prev => prev.map(n => n.id === noteId ? { ...n, color } : n));
  };

  const handleDeleteNote = (noteId, e) => {
    if (e) e.stopPropagation();
    triggerConfirm(
      '이 메모를 삭제하시겠습니까?',
      () => {
        setStickyNotes(prev => prev.filter(n => n.id !== noteId));
      },
      '메모 삭제'
    );
  };

  const handleNoteDoubleClick = (noteId) => {
    setEditingNoteId(noteId);
  };

  // --- 스티커 메모 마우스 및 터치 이벤트 핸들러 ---
  const handleNoteMouseDown = (noteId, e) => {
    if (e.button !== 0) return;
    if (e.target.classList.contains('note-resize-handle') || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.closest('.note-colors')) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    const note = stickyNotes.find(n => n.id === noteId);
    if (!note) return;

    const isSelected = selectedNoteIds.includes(noteId);
    let nextTableIds = selectedTableIds;
    let nextNoteIds = selectedNoteIds;

    if (!isSelected) {
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        nextNoteIds = [...selectedNoteIds, noteId];
      } else {
        nextTableIds = [];
        nextNoteIds = [noteId];
        setSelectedTableId(null);
      }
      setSelectedTableIds(nextTableIds);
      setSelectedNoteIds(nextNoteIds);
    } else {
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        nextNoteIds = selectedNoteIds.filter(id => id !== noteId);
        setSelectedNoteIds(nextNoteIds);
      }
    }

    const initTables = tables.map(t => ({ id: t.id, x: t.x, y: t.y }));
    const initNotes = stickyNotes.map(n => ({ id: n.id, x: n.x, y: n.y }));

    setDragState({
      type: 'multi-drag',
      startX: e.clientX,
      startY: e.clientY,
      initTables,
      initNotes,
      draggedNoteId: noteId,
      clickOnly: isSelected && !(e.ctrlKey || e.metaKey || e.shiftKey)
    });
  };

  const handleNoteTouchStart = (noteId, e) => {
    if (e.target.classList.contains('note-resize-handle') || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.closest('.note-colors')) {
      return;
    }
    e.stopPropagation();
    const note = stickyNotes.find(n => n.id === noteId);
    if (!note) return;
    const touch = e.touches[0];

    const initTables = tables.map(t => ({ id: t.id, x: t.x, y: t.y }));
    const initNotes = stickyNotes.map(n => ({ id: n.id, x: n.x, y: n.y }));

    if (!selectedNoteIds.includes(noteId)) {
      setSelectedTableIds([]);
      setSelectedNoteIds([noteId]);
      setSelectedTableId(null);
    }

    setDragState({
      type: 'multi-drag',
      startX: touch.clientX,
      startY: touch.clientY,
      initTables,
      initNotes,
      draggedNoteId: noteId
    });
  };

  const handleNoteResizeMouseDown = (noteId, e) => {
    e.stopPropagation();
    e.preventDefault();
    const note = stickyNotes.find(n => n.id === noteId);
    if (!note) return;

    setDragState({
      type: 'note-resize',
      noteId,
      startX: e.clientX,
      startY: e.clientY,
      initW: note.w,
      initH: note.h
    });
  };

  const handleNoteResizeTouchStart = (noteId, e) => {
    e.stopPropagation();
    const note = stickyNotes.find(n => n.id === noteId);
    if (!note) return;
    const touch = e.touches[0];

    setDragState({
      type: 'note-resize',
      noteId,
      startX: touch.clientX,
      startY: touch.clientY,
      initW: note.w,
      initH: note.h
    });
  };

  const handleLoadSample = (sampleType) => {
    let sample = null;
    if (sampleType === 'basic') sample = SAMPLE_BASIC;
    else if (sampleType === 'ecommerce') sample = SAMPLE_ECOMMERCE;
    else if (sampleType === 'lms') sample = SAMPLE_LMS;

    if (!sample) return;

    triggerConfirm(
      '현재 작업 중인 설계를 초기화하고 선택한 샘플 ERD를 로드하시겠습니까?',
      () => {
        setTables(sample.tables);
        setRelationships(sample.relationships);
        setStickyNotes(sample.stickyNotes);
        setSelectedDb(sample.dbType);
        setCanvasPan(sample.canvasPan);
        setCanvasScale(sample.canvasScale);

        setProjectId(null);
        setProjectTitle('');
        setProjectDescription('');
        setCurrentVersionNo(null);

        triggerAlert('샘플 다이어그램을 성공적으로 로드했습니다!', '로드 완료', 'success');
      },
      '샘플 로드'
    );
  };

  const handleNewDiagram = () => {
    triggerConfirm(
      '현재 작업 중인 다이어그램을 초기화하고 새 설계를 시작하시겠습니까?',
      () => {
        setTables(INITIAL_TABLES);
        setRelationships(INITIAL_RELATIONSHIPS);
        setStickyNotes([]);
        setProjectId(null);
        setProjectTitle('');
        setProjectDescription('');
        setCurrentVersionNo(null);
        localStorage.removeItem('wajok_erd_project_id');
        localStorage.removeItem('wajok_erd_project_title');
        localStorage.removeItem('wajok_erd_project_desc');
        localStorage.removeItem('wajok_erd_version_no');
        triggerAlert('새 다이어그램이 활성화되었습니다.', '신규 설계 시작', 'success');
      },
      '새 다이어그램'
    );
  };

  const handleCloudSaveSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!projectId && !saveTitle.trim()) {
      triggerAlert('다이어그램 제목을 필수 기입해 주세요.', '입력 오류', 'warning');
      return;
    }

    try {
      const payload = {
        reqType: projectId ? 'update' : 'create',
        member_id: user?.id,
        project_id: projectId,
        title: projectId ? projectTitle : saveTitle,
        description: projectId ? projectDescription : saveDesc,
        save_message: saveCommitMsg || (projectId ? '버전 업데이트' : '최초 생성'),
        erd_data: {
          dbType: selectedDb,
          canvasPan,
          canvasScale,
          tables,
          relationships,
          stickyNotes
        }
      };

      const response = await axiosInstance.post('erd', payload);
      if (response.data.status === '0000') {
        triggerAlert('성공적으로 클라우드에 저장되었습니다!', '저장 성공', 'success');
        const data = response.data.data;
        const projData = Array.isArray(data) ? data[0] : data;

        if (!projectId) {
          const newId = projData?.project_id || projData?.id || 1;
          setProjectId(newId);
          setProjectTitle(saveTitle);
          setProjectDescription(saveDesc);
          setCurrentVersionNo(1);
        } else {
          setCurrentVersionNo(prev => (prev ? prev + 1 : 2));
        }

        setShowCloudSaveModal(false);
        setSaveCommitMsg('');
        setSaveTitle('');
        setSaveDesc('');
      } else {
        triggerAlert('저장 실패: ' + (response.data.message || '알 수 없는 오류'), '저장 실패', 'danger');
      }
    } catch (err) {
      console.error(err);
      triggerAlert('클라우드 저장 처리 중 장애가 발생했습니다.', '저장 에러', 'danger');
    }
  };

  const fetchCloudProjects = async () => {
    if (!isLoggedIn) return;
    setLoadingProjects(true);
    try {
      const response = await axiosInstance.post('erd', {
        reqType: 'list',
        member_id: user?.id
      });
      if (response.data?.status === '0000') {
        const rawList = response.data.data?.list || [];
        const formattedList = rawList
          .map(item => item.json)
          .filter(json => json && Object.keys(json).length > 0 && (json.project_id || json.id));
        setCloudProjects(formattedList);
      } else {
        setCloudProjects([]);
      }
    } catch (err) {
      console.error(err);
      setCloudProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleLoadProject = async (projId) => {
    try {
      const response = await axiosInstance.post('erd', {
        reqType: 'detail',
        project_id: projId
      });
      if (response.data?.status === '0000') {
        const detailList = response.data.data?.detail;
        const detailObj = Array.isArray(detailList) ? detailList[0] : null;
        const jsonVal = detailObj?.json;
        const erd = jsonVal?.erd_data;

        if (erd && Object.keys(jsonVal).length > 0) {
          const proj = cloudProjects.find(p => p.project_id === projId);
          setProjectId(projId);
          setProjectTitle(proj?.title || '로드된 프로젝트');
          setProjectDescription(proj?.description || '');
          setCurrentVersionNo(proj?.latest_version || 1);

          if (erd.tables) setTables(erd.tables);
          if (erd.relationships) setRelationships(erd.relationships);
          if (erd.stickyNotes) setStickyNotes(erd.stickyNotes || []);
          if (erd.dbType) setSelectedDb(erd.dbType);
          if (erd.canvasPan) setCanvasPan(erd.canvasPan);
          if (erd.canvasScale) setCanvasScale(erd.canvasScale);

          setShowCloudLoadModal(false);
          triggerAlert('다이어그램 스키마를 클라우드로부터 성공적으로 불렀습니다!', '로드 성공', 'success');
        } else {
          triggerAlert('가져올 다이어그램 데이터가 존재하지 않습니다.', '데이터 없음', 'warning');
        }
      } else {
        triggerAlert('데이터 로드 실패', '로드 실패', 'danger');
      }
    } catch (err) {
      console.error(err);
      triggerAlert('통신 장애가 발생했습니다.', '통신 에러', 'danger');
    }
  };

  const fetchProjectHistory = async () => {
    if (!projectId) return;
    setLoadingHistory(true);
    try {
      const response = await axiosInstance.post('erd', {
        reqType: 'history',
        project_id: projectId
      });
      if (response.data?.status === '0000') {
        const rawHistory = response.data.data?.history || [];
        const formattedHistory = rawHistory
          .map(item => item.json)
          .filter(Boolean);
        setHistoryList(formattedHistory);
      } else {
        setHistoryList([]);
      }
    } catch (err) {
      console.error(err);
      setHistoryList([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleLoadVersion = async (vNo) => {
    try {
      const response = await axiosInstance.post('erd', {
        reqType: 'version',
        project_id: projectId,
        version_no: vNo
      });
      if (response.data?.status === '0000') {
        const versionList = response.data.data?.version;
        const versionObj = Array.isArray(versionList) ? versionList[0] : null;
        const jsonVal = versionObj?.json;
        const erd = jsonVal?.erd_data;

        if (erd && Object.keys(jsonVal).length > 0) {
          setCurrentVersionNo(vNo);

          if (erd.tables) setTables(erd.tables);
          if (erd.relationships) setRelationships(erd.relationships);
          if (erd.stickyNotes) setStickyNotes(erd.stickyNotes || []);
          if (erd.dbType) setSelectedDb(erd.dbType);
          if (erd.canvasPan) setCanvasPan(erd.canvasPan);
          if (erd.canvasScale) setCanvasScale(erd.canvasScale);

          setShowCloudHistoryModal(false);
          triggerAlert(`성공적으로 버전 v${vNo} 상태로 복원했습니다!`, '복원 성공', 'success');
        } else {
          triggerAlert('버전 데이터가 만료되었거나 비어있습니다.', '복원 경고', 'warning');
        }
      } else {
        triggerAlert('버전 복원 실패', '복원 실패', 'danger');
      }
    } catch (err) {
      console.error(err);
      triggerAlert('버전 복원 중 통신 에러 발생', '통신 에러', 'danger');
    }
  };

  const handleCloudDelete = (projId, e) => {
    if (e) e.stopPropagation();
    triggerConfirm(
      '이 다이어그램 프로젝트를 클라우드 저장소에서 영구 삭제하시겠습니까? 관련된 버전 히스토리가 전부 날아갑니다.',
      async () => {
        try {
          const response = await axiosInstance.post('erd', {
            reqType: 'delete',
            project_id: projId,
            member_id: user?.id
          });
          if (response.data.status === '0000') {
            triggerAlert('삭제되었습니다.', '삭제 성공', 'success');
            if (projectId === projId) {
              setTables(INITIAL_TABLES);
              setRelationships(INITIAL_RELATIONSHIPS);
              setStickyNotes([]);
              setProjectId(null);
              setProjectTitle('');
              setProjectDescription('');
              setCurrentVersionNo(null);
              localStorage.removeItem('wajok_erd_project_id');
              localStorage.removeItem('wajok_erd_project_title');
              localStorage.removeItem('wajok_erd_project_desc');
              localStorage.removeItem('wajok_erd_version_no');
            }
            fetchCloudProjects();
          } else {
            triggerAlert('삭제에 실패했습니다: ' + (response.data.message || ''), '삭제 실패', 'danger');
          }
        } catch (err) {
          console.error(err);
          triggerAlert('삭제 실행 중 에러가 발생했습니다.', '삭제 에러', 'danger');
        }
      },
      '프로젝트 삭제'
    );
  };

  // --- 캔버스 배경 드래그 (패닝) 마우스 이벤트 ---
  const handleCanvasMouseDown = (e) => {
    if (e.target.classList.contains('erd-canvas') || e.target.classList.contains('erd-canvas-container')) {
      const isRightClick = e.button === 2;
      const isPan = isRightClick || e.shiftKey;

      if (isPan) {
        setDragState({
          type: 'pan',
          startX: e.clientX,
          startY: e.clientY,
          initX: canvasPan.x,
          initY: canvasPan.y
        });
      } else {
        // Start selection marquee
        const containerRect = canvasContainerRef.current.getBoundingClientRect();
        const canvasMouseX = (e.clientX - containerRect.left - canvasPan.x) / canvasScale;
        const canvasMouseY = (e.clientY - containerRect.top - canvasPan.y) / canvasScale;

        setDragState({
          type: 'select',
          startX: e.clientX,
          startY: e.clientY,
          canvasStartX: canvasMouseX,
          canvasStartY: canvasMouseY,
          canvasCurX: canvasMouseX,
          canvasCurY: canvasMouseY
        });

        // Clear current selection unless Shift/Ctrl is held for additive selection
        if (!e.metaKey && !e.ctrlKey) {
          setSelectedTableIds([]);
          setSelectedNoteIds([]);
          setSelectedTableId(null);
        }
      }
    }
  };

  const handleContextMenu = (e) => {
    if (e.target.classList.contains('erd-canvas') || e.target.classList.contains('erd-canvas-container')) {
      e.preventDefault();
    }
  };

  // --- 컬럼 드래그 앤 드롭 순서 변경 핸들러 ---
  const handleColDragStart = (e, tableId, colId, index) => {
    e.stopPropagation();
    setDraggedColInfo({ tableId, colId, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColDragOver = (e, tableId, index) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleColDrop = (e, tableId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedColInfo || draggedColInfo.tableId !== tableId) return;

    const sourceIndex = draggedColInfo.index;
    if (sourceIndex === targetIndex) return;

    setTables(prevTables => prevTables.map(t => {
      if (t.id === tableId) {
        const newCols = [...t.columns];
        const [movedCol] = newCols.splice(sourceIndex, 1);
        newCols.splice(targetIndex, 0, movedCol);
        return {
          ...t,
          columns: newCols
        };
      }
      return t;
    }));
    setDraggedColInfo(null);
  };

  const handleColDragEnd = () => {
    setDraggedColInfo(null);
  };

  // --- 모바일 터치 이벤트 핸들러 추가 ---
  const handleTableHeaderTouchStart = (tableId, e) => {
    e.stopPropagation();
    const table = tables.find(t => t.id === tableId);
    if (!table) return;
    const touch = e.touches[0];

    const initTables = tables.map(t => ({ id: t.id, x: t.x, y: t.y }));
    const initNotes = stickyNotes.map(n => ({ id: n.id, x: n.x, y: n.y }));

    if (!selectedTableIds.includes(tableId)) {
      setSelectedTableIds([tableId]);
      setSelectedNoteIds([]);
      setSelectedTableId(tableId);
    }

    setDragState({
      type: 'multi-drag',
      startX: touch.clientX,
      startY: touch.clientY,
      initTables,
      initNotes,
      draggedTableId: tableId
    });
  };

  const handleConnectorTouchStart = (tableId, columnId, e) => {
    e.stopPropagation();
    const table = tables.find(t => t.id === tableId);
    const colIndex = table.columns.findIndex(c => c.id === columnId);

    const cardWidth = 260;
    const headerHeight = 52;
    const rowHeight = 30;
    const startX = table.x + cardWidth;
    const startY = table.y + headerHeight + (colIndex * rowHeight) + (rowHeight / 2);

    setDragState({
      type: 'connect',
      parentTableId: tableId,
      parentColumnId: columnId,
      startX,
      startY,
      curX: startX,
      curY: startY
    });
  };

  const handleCanvasTouchStart = (e) => {
    if (e.target.classList.contains('erd-canvas') || e.target.classList.contains('erd-canvas-container')) {
      const touch = e.touches[0];
      setDragState({
        type: 'pan',
        startX: touch.clientX,
        startY: touch.clientY,
        initX: canvasPan.x,
        initY: canvasPan.y
      });
    }
  };

  const handleTouchMove = useCallback((e) => {
    if (!dragState) return;

    // 모바일 드래깅 중 브라우저 기본 스크롤 동작 방지
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];

    if (dragState.type === 'multi-drag') {
      const dx = (touch.clientX - dragState.startX) / canvasScale;
      const dy = (touch.clientY - dragState.startY) / canvasScale;

      setTables(prev => prev.map(t => {
        if (selectedTableIds.includes(t.id)) {
          const initPos = dragState.initTables.find(it => it.id === t.id);
          if (initPos) {
            const nextX = Math.max(0, Math.round((initPos.x + dx) / 10) * 10);
            const nextY = Math.max(0, Math.round((initPos.y + dy) / 10) * 10);
            return { ...t, x: nextX, y: nextY };
          }
        }
        return t;
      }));

      setStickyNotes(prev => prev.map(n => {
        if (selectedNoteIds.includes(n.id)) {
          const initPos = dragState.initNotes.find(inote => inote.id === n.id);
          if (initPos) {
            const nextX = Math.max(0, Math.round((initPos.x + dx) / 10) * 10);
            const nextY = Math.max(0, Math.round((initPos.y + dy) / 10) * 10);
            return { ...n, x: nextX, y: nextY };
          }
        }
        return n;
      }));
    } else if (dragState.type === 'note-resize') {
      const dx = (touch.clientX - dragState.startX) / canvasScale;
      const dy = (touch.clientY - dragState.startY) / canvasScale;

      setStickyNotes(prev => prev.map(n => {
        if (n.id === dragState.noteId) {
          const nextW = Math.max(120, Math.round((dragState.initW + dx) / 10) * 10);
          const nextH = Math.max(80, Math.round((dragState.initH + dy) / 10) * 10);
          return { ...n, w: nextW, h: nextH };
        }
        return n;
      }));
    } else if (dragState.type === 'pan') {
      const dx = touch.clientX - dragState.startX;
      const dy = touch.clientY - dragState.startY;
      setCanvasPan({
        x: dragState.initX + dx,
        y: dragState.initY + dy
      });
    } else if (dragState.type === 'connect') {
      const containerRect = canvasContainerRef.current.getBoundingClientRect();
      const canvasMouseX = (touch.clientX - containerRect.left - canvasPan.x) / canvasScale;
      const canvasMouseY = (touch.clientY - containerRect.top - canvasPan.y) / canvasScale;

      setDragState(prev => ({
        ...prev,
        curX: canvasMouseX,
        curY: canvasMouseY
      }));
    }
  }, [dragState, canvasScale, canvasPan, selectedTableIds, selectedNoteIds]);

  const handleTouchEnd = useCallback((e) => {
    if (!dragState) return;

    if (dragState.type === 'connect') {
      const touch = e.changedTouches[0];
      if (touch) {
        const containerRect = canvasContainerRef.current.getBoundingClientRect();
        const canvasMouseX = (touch.clientX - containerRect.left - canvasPan.x) / canvasScale;
        const canvasMouseY = (touch.clientY - containerRect.top - canvasPan.y) / canvasScale;

        const hoverTable = tables.find(t => {
          const height = 52 + t.columns.length * 30;
          return (
            canvasMouseX >= t.x && canvasMouseX <= t.x + 260 &&
            canvasMouseY >= t.y && canvasMouseY <= t.y + height
          );
        });

        if (hoverTable && hoverTable.id !== dragState.parentTableId) {
          const parentTable = tables.find(t => t.id === dragState.parentTableId);
          const parentCol = parentTable.columns.find(c => c.id === dragState.parentColumnId);

          const fkColName = `${parentTable.physicalName}_${parentCol.physicalName}`;

          // 중복명 방지 및 얼럿 얼리 리턴
          const isExist = hoverTable.columns.some(c => c.physicalName === fkColName);
          if (isExist) {
            triggerAlert('동일한 이름의 컬럼이 이미 존재합니다.', '중복 오류', 'warning');
            setDragState(null);
            return;
          }

          const fkColLogicalName = `${parentTable.logicalName} ${parentCol.logicalName}`;
          const fkColId = 'c_' + Date.now();

          let childDataType = parentCol.dataType;
          if (childDataType.toUpperCase().includes('SERIAL')) childDataType = 'INTEGER';
          if (childDataType.toUpperCase().includes('AUTO_INCREMENT')) childDataType = 'INT';
          if (childDataType.toUpperCase().includes('IDENTITY')) childDataType = 'INT';
          if (childDataType.toUpperCase().includes('AUTOINCREMENT')) childDataType = 'INTEGER';

          const newFkCol = {
            id: fkColId,
            physicalName: fkColName,
            logicalName: fkColLogicalName,
            dataType: childDataType,
            size: parentCol.size || '',
            pk: false,
            fk: true,
            notNull: true,
            unique: false,
            defaultValue: '',
            comment: `${parentTable.physicalName}.${parentCol.physicalName} 참조 외래키`,
            fkRef: {
              tableId: dragState.parentTableId,
              columnId: dragState.parentColumnId
            }
          };

          setTables(prev => prev.map(t => {
            if (t.id === hoverTable.id) {
              return {
                ...t,
                columns: [...t.columns, newFkCol]
              };
            }
            return t;
          }));

          setRelationships(prev => [
            ...prev,
            {
              id: 'r_' + Date.now(),
              parentTableId: dragState.parentTableId,
              parentColumnId: dragState.parentColumnId,
              childTableId: hoverTable.id,
              childColumnId: fkColId,
              type: '1:N'
            }
          ]);
        }
      }
    }

    setDragState(null);
  }, [dragState, tables, canvasScale, canvasPan, triggerAlert]);

  // --- 전역 마우스 무브 / 업 제어 ---
  const handleMouseMove = useCallback((e) => {
    if (!dragState) return;

    if (dragState.type === 'multi-drag') {
      const dx = (e.clientX - dragState.startX) / canvasScale;
      const dy = (e.clientY - dragState.startY) / canvasScale;

      setTables(prev => prev.map(t => {
        if (selectedTableIds.includes(t.id)) {
          const initPos = dragState.initTables.find(it => it.id === t.id);
          if (initPos) {
            const nextX = Math.max(0, Math.round((initPos.x + dx) / 10) * 10);
            const nextY = Math.max(0, Math.round((initPos.y + dy) / 10) * 10);
            return { ...t, x: nextX, y: nextY };
          }
        }
        return t;
      }));

      setStickyNotes(prev => prev.map(n => {
        if (selectedNoteIds.includes(n.id)) {
          const initPos = dragState.initNotes.find(inote => inote.id === n.id);
          if (initPos) {
            const nextX = Math.max(0, Math.round((initPos.x + dx) / 10) * 10);
            const nextY = Math.max(0, Math.round((initPos.y + dy) / 10) * 10);
            return { ...n, x: nextX, y: nextY };
          }
        }
        return n;
      }));
    } else if (dragState.type === 'select') {
      const containerRect = canvasContainerRef.current.getBoundingClientRect();
      const canvasMouseX = (e.clientX - containerRect.left - canvasPan.x) / canvasScale;
      const canvasMouseY = (e.clientY - containerRect.top - canvasPan.y) / canvasScale;

      setDragState(prev => ({
        ...prev,
        canvasCurX: canvasMouseX,
        canvasCurY: canvasMouseY
      }));

      // Calculate selection box boundaries in canvas coordinates
      const x1 = Math.min(dragState.canvasStartX, canvasMouseX);
      const x2 = Math.max(dragState.canvasStartX, canvasMouseX);
      const y1 = Math.min(dragState.canvasStartY, canvasMouseY);
      const y2 = Math.max(dragState.canvasStartY, canvasMouseY);

      // Find all tables within selection box
      const newSelTableIds = [];
      tables.forEach(t => {
        const tWidth = 260;
        const tHeight = 52 + t.columns.length * 30;
        if (t.x + tWidth >= x1 && t.x <= x2 && t.y + tHeight >= y1 && t.y <= y2) {
          newSelTableIds.push(t.id);
        }
      });

      // Find all notes within selection box
      const newSelNoteIds = [];
      stickyNotes.forEach(n => {
        if (n.x + n.w >= x1 && n.x <= x2 && n.y + n.h >= y1 && n.y <= y2) {
          newSelNoteIds.push(n.id);
        }
      });

      setSelectedTableIds(newSelTableIds);
      setSelectedNoteIds(newSelNoteIds);

      if (newSelTableIds.length > 0) {
        setSelectedTableId(newSelTableIds[0]);
      } else {
        setSelectedTableId(null);
      }
    } else if (dragState.type === 'note-resize') {
      const dx = (e.clientX - dragState.startX) / canvasScale;
      const dy = (e.clientY - dragState.startY) / canvasScale;

      setStickyNotes(prev => prev.map(n => {
        if (n.id === dragState.noteId) {
          const nextW = Math.max(120, Math.round((dragState.initW + dx) / 10) * 10);
          const nextH = Math.max(80, Math.round((dragState.initH + dy) / 10) * 10);
          return { ...n, w: nextW, h: nextH };
        }
        return n;
      }));
    } else if (dragState.type === 'pan') {
      const dx = e.clientX - dragState.startX;
      const dy = e.clientY - dragState.startY;
      setCanvasPan({
        x: dragState.initX + dx,
        y: dragState.initY + dy
      });
    } else if (dragState.type === 'connect') {
      // 캔버스 내 마우스 좌표로 환산
      const containerRect = canvasContainerRef.current.getBoundingClientRect();
      const canvasMouseX = (e.clientX - containerRect.left - canvasPan.x) / canvasScale;
      const canvasMouseY = (e.clientY - containerRect.top - canvasPan.y) / canvasScale;

      setDragState(prev => ({
        ...prev,
        curX: canvasMouseX,
        curY: canvasMouseY
      }));
    }
  }, [dragState, canvasScale, canvasPan, tables, stickyNotes, selectedTableIds, selectedNoteIds]);

  const handleMouseUp = useCallback((e) => {
    if (!dragState) return;

    if (dragState.type === 'multi-drag' && dragState.clickOnly) {
      const dx = Math.abs(e.clientX - dragState.startX);
      const dy = Math.abs(e.clientY - dragState.startY);
      if (dx < 3 && dy < 3) {
        if (dragState.draggedTableId) {
          setSelectedTableIds([dragState.draggedTableId]);
          setSelectedNoteIds([]);
          setSelectedTableId(dragState.draggedTableId);
        } else if (dragState.draggedNoteId) {
          setSelectedTableIds([]);
          setSelectedNoteIds([dragState.draggedNoteId]);
          setSelectedTableId(null);
        }
      }
    } else if (dragState.type === 'connect') {
      // 마우스가 놓인 위치의 테이블을 찾아서 관계성 수립
      const containerRect = canvasContainerRef.current.getBoundingClientRect();
      const canvasMouseX = (e.clientX - containerRect.left - canvasPan.x) / canvasScale;
      const canvasMouseY = (e.clientY - containerRect.top - canvasPan.y) / canvasScale;

      // 어느 테이블 좌표 안에 드롭되었는지 체크
      const hoverTable = tables.find(t => {
        const height = 52 + t.columns.length * 30; // 헤더 + 컬럼수 * 높이
        return (
          canvasMouseX >= t.x && canvasMouseX <= t.x + 260 &&
          canvasMouseY >= t.y && canvasMouseY <= t.y + height
        );
      });

      if (hoverTable && hoverTable.id !== dragState.parentTableId) {
        // 이미 맺어진 관계가 아니면 신설
        const parentTable = tables.find(t => t.id === dragState.parentTableId);
        const parentCol = parentTable.columns.find(c => c.id === dragState.parentColumnId);

        // 자식 테이블에 FK 생성
        const fkColName = `${parentTable.physicalName}_${parentCol.physicalName}`;

        // 중복명 방지 및 얼럿 얼리 리턴
        const isExist = hoverTable.columns.some(c => c.physicalName === fkColName);
        if (isExist) {
          triggerAlert('동일한 이름의 컬럼이 이미 존재합니다.', '중복 오류', 'warning');
          setDragState(null);
          return;
        }

        const fkColLogicalName = `${parentTable.logicalName} ${parentCol.logicalName}`;
        const fkColId = 'c_' + Date.now();

        // 데이터타입은 부모 PK타입에서 SERIAL 속성을 제거한 정수/VARCHAR 등으로 대응
        let childDataType = parentCol.dataType;
        if (childDataType.toUpperCase().includes('SERIAL')) childDataType = 'INTEGER';
        if (childDataType.toUpperCase().includes('AUTO_INCREMENT')) childDataType = 'INT';
        if (childDataType.toUpperCase().includes('IDENTITY')) childDataType = 'INT';
        if (childDataType.toUpperCase().includes('AUTOINCREMENT')) childDataType = 'INTEGER';

        const newFkCol = {
          id: fkColId,
          physicalName: fkColName,
          logicalName: fkColLogicalName,
          dataType: childDataType,
          size: parentCol.size || '',
          pk: false,
          fk: true,
          notNull: true,
          unique: false,
          defaultValue: '',
          comment: `${parentTable.physicalName}.${parentCol.physicalName} 참조 외래키`,
          fkRef: {
            tableId: dragState.parentTableId,
            columnId: dragState.parentColumnId
          }
        };

        // 자식 테이블에 컬럼 주입
        setTables(prev => prev.map(t => {
          if (t.id === hoverTable.id) {
            return {
              ...t,
              columns: [...t.columns, newFkCol]
            };
          }
          return t;
        }));

        // 관계 목록 등록
        setRelationships(prev => [
          ...prev,
          {
            id: 'r_' + Date.now(),
            parentTableId: dragState.parentTableId,
            parentColumnId: dragState.parentColumnId,
            childTableId: hoverTable.id,
            childColumnId: fkColId,
            type: '1:N'
          }
        ]);
      }
    }

    setDragState(null);
  }, [dragState, tables, canvasScale, canvasPan, triggerAlert]);

  // 전역 마우스/터치 무브 및 업 이벤트 리스너 바인딩
  useEffect(() => {
    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [dragState, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // --- Ctrl + Wheel 줌인/줌아웃 핸들러 (마우스 위치 기준) ---
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();

        const containerRect = container.getBoundingClientRect();
        const px = e.clientX - containerRect.left;
        const py = e.clientY - containerRect.top;

        const zoomStep = 0.05;
        const factor = e.deltaY < 0 ? zoomStep : -zoomStep;

        setCanvasScale(prevScale => {
          const nextScale = Math.min(1.5, Math.max(0.5, prevScale + factor));
          if (nextScale === prevScale) return prevScale;

          setCanvasPan(prevPan => {
            const ratio = nextScale / prevScale;
            const newPanX = px - (px - prevPan.x) * ratio;
            const newPanY = py - (py - prevPan.y) * ratio;
            return { x: Math.round(newPanX), y: Math.round(newPanY) };
          });

          return nextScale;
        });
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // --- 테이블 편집 모달 실행 ---
  const handleOpenEditModal = (table, e) => {
    if (e) e.stopPropagation();
    // 딥카피하여 임시로 모달 폼 제어
    setEditingTable(JSON.parse(JSON.stringify(table)));
    setShowTableModal(true);
  };

  const handleValidateTable = () => {
    if (!editingTable) return;
    
    const errors = [];
    const warnings = [];
    let fixedTable = JSON.parse(JSON.stringify(editingTable));
    let fixedAny = false;

    // 1. 테이블 물리명 검사
    if (!editingTable.physicalName || !editingTable.physicalName.trim()) {
      errors.push("테이블 물리명이 공백입니다.");
    } else {
      const cleanPhysName = editingTable.physicalName.replace(/[^a-zA-Z0-9_]/g, '');
      if (cleanPhysName !== editingTable.physicalName) {
        warnings.push(`테이블 물리명에 특수문자나 한글이 포함되어 있습니다. ('${cleanPhysName}'으로 자동 보정 가능)`);
        fixedTable.physicalName = cleanPhysName;
        fixedAny = true;
      }
    }

    // 2. 컬럼 검사
    const colNames = new Set();
    editingTable.columns.forEach((col, idx) => {
      const colNum = idx + 1;
      const colLabel = col.physicalName ? `'${col.physicalName}'` : `${colNum}번째 컬럼`;

      // 2.1 컬럼 물리명 누락
      if (!col.physicalName || !col.physicalName.trim()) {
        errors.push(`${colNum}번째 컬럼의 물리명이 비어 있습니다.`);
      } else {
        const cleanColName = col.physicalName.replace(/[^a-zA-Z0-9_]/g, '');
        if (cleanColName !== col.physicalName) {
          warnings.push(`컬럼 물리명 ${colLabel}에 허용되지 않는 문자가 있습니다. ('${cleanColName}'으로 자동 보정 가능)`);
          fixedTable.columns[idx].physicalName = cleanColName;
          fixedAny = true;
        }
        
        // 2.2 컬럼 물리명 중복
        const lowerName = col.physicalName.toLowerCase();
        if (colNames.has(lowerName)) {
          errors.push(`컬럼 물리명 ${colLabel}이 중복되었습니다.`);
        } else {
          colNames.add(lowerName);
        }
      }

      // 2.3 데이터 타입 누락
      if (!col.dataType || !col.dataType.trim()) {
        errors.push(`컬럼 ${colLabel}의 데이터 타입이 지정되지 않았습니다.`);
      }

      // 2.4 길이 지정 불가 타입 검사
      if (!doesTypeSupportSize(col.dataType)) {
        const cleanedSz = col.size ? col.size.toString().replace(/['"]/g, '').trim() : '';
        if (cleanedSz !== '') {
          warnings.push(`컬럼 ${colLabel}의 타입('${col.dataType}')은 길이를 지정할 필요가 없습니다. (입력된 값 '${col.size}' 자동 제거 가능)`);
          fixedTable.columns[idx].size = '';
          fixedAny = true;
        }
      } else {
        // 2.5 길이 지정 지원 타입인데 값이 비어있거나 올바르지 않은 경우
        const cleanedSz = col.size ? col.size.toString().replace(/['"]/g, '').trim() : '';
        const tUpper = col.dataType.toUpperCase();
        if ((tUpper.startsWith('VARCHAR') || tUpper.startsWith('CHAR')) && cleanedSz === '') {
          warnings.push(`문자형 컬럼 ${colLabel}의 크기(길이)가 비어있습니다. (기본값 100 또는 255 권장)`);
        } else if (cleanedSz !== '' && isNaN(cleanedSz) && cleanedSz.toLowerCase() !== 'max') {
          const isDecimalFormat = /^\d+(,\d+)?$/.test(cleanedSz);
          if (!isDecimalFormat) {
            errors.push(`컬럼 ${colLabel}의 길이값 '${col.size}' 형식이 유효하지 않습니다. (숫자 또는 'max', 소수점 표현인 '10,2' 등이어야 합니다)`);
          }
        }
      }

      // 2.6 기본값 불필요한 따옴표 제거 보정
      if (col.defaultValue && col.defaultValue.trim() !== '') {
        const val = col.defaultValue.trim();
        const isQuoted = (val.startsWith("'") && val.endsWith("'")) || (val.startsWith('"') && val.endsWith('"'));
        if (isQuoted) {
          warnings.push(`컬럼 ${colLabel}의 기본값에 불필요한 따옴표가 입력되어 있습니다. (따옴표 제거 자동 보정 가능)`);
          fixedTable.columns[idx].defaultValue = val.replace(/^['"]|['"]$/g, '');
          fixedAny = true;
        }
      }
    });

    // 3. PK 존재 여부
    const hasPk = editingTable.columns.some(c => c.pk);
    if (!hasPk) {
      warnings.push("테이블에 기본키(PK)가 지정되지 않았습니다. (지정을 권장합니다)");
    }

    if (errors.length === 0 && warnings.length === 0) {
      triggerAlert(
        "기본 유효성 검사를 통과했습니다! 설계에 이상이 없습니다.",
        "설계 검사 성공",
        "success"
      );
    } else {
      let msg = "";
      if (errors.length > 0) {
        msg += "⚠️ [오류 목록]\n" + errors.map(e => "- " + e).join("\n") + "\n\n";
      }
      if (warnings.length > 0) {
        msg += "💡 [경고 및 보정 제안]\n" + warnings.map(w => "- " + w).join("\n") + "\n";
      }

      if (fixedAny) {
        msg += "\n* 하단의 '자동 보정 적용' 버튼을 클릭하여 경고가 지적한 문제점들을 한 번에 수정할 수 있습니다.";
        
        triggerConfirm(
          msg,
          () => {
            setEditingTable(fixedTable);
            triggerAlert("권장 보정안이 설계 창에 적용되었습니다. 내용을 확인하신 후 저장해 주세요.", "자동 보정 완료", "success");
          },
          "설계 유효성 검사 결과 (보정 가능)",
          null
        );
      } else {
        triggerAlert(
          msg,
          errors.length > 0 ? "설계 검사 실패 (수정 필요)" : "설계 검사 권장 알림",
          errors.length > 0 ? "danger" : "warning"
        );
      }
    }
  };

  const handleSaveTableModal = () => {
    if (!editingTable.physicalName.trim()) {
      triggerAlert('테이블 물리명을 입력해 주세요.', '입력 오류', 'warning');
      return;
    }

    // 물리명 중복 검사
    const duplicate = tables.some(t => t.id !== editingTable.id && t.physicalName.toLowerCase() === editingTable.physicalName.toLowerCase());
    if (duplicate) {
      triggerAlert('이미 사용중인 테이블 물리명입니다.', '중복 오류', 'warning');
      return;
    }

    // Clean sizes and default values before saving
    const cleanedTable = {
      ...editingTable,
      columns: editingTable.columns.map(col => {
        const cleanSize = (sz) => {
          if (sz === undefined || sz === null) return '';
          const s = sz.toString().trim();
          if (s === "''" || s === '""' || s === '""' || s === "''" || s === '') return '';
          return s.replace(/['"]/g, '').trim();
        };
        let cleanDef = col.defaultValue ? col.defaultValue.toString().trim() : '';
        if (cleanDef) {
          cleanDef = cleanDef.replace(/^['"]|['"]$/g, '');
        }
        return {
          ...col,
          size: cleanSize(col.size),
          defaultValue: cleanDef
        };
      })
    };

    setTables(prev => prev.map(t => t.id === cleanedTable.id ? cleanedTable : t));
    setShowTableModal(false);
    setEditingTable(null);
  };

  const handleModalAddColumn = () => {
    const colId = 'c_' + Date.now();
    const newCol = {
      id: colId,
      physicalName: `column_${editingTable.columns.length + 1}`,
      logicalName: `새컬럼_${editingTable.columns.length + 1}`,
      dataType: selectedDb === 'SQLite' ? 'TEXT' : 'VARCHAR',
      size: selectedDb === 'SQLite' ? '' : '100',
      pk: false,
      fk: false,
      notNull: false,
      unique: false,
      defaultValue: '',
      comment: ''
    };
    setEditingTable({
      ...editingTable,
      columns: [...editingTable.columns, newCol]
    });
  };

  const handleModalDeleteColumn = (colId) => {
    const col = editingTable.columns.find(c => c.id === colId);
    if (col && col.fk) {
      const hasRelation = relationships.some(r => r.childTableId === editingTable.id && r.childColumnId === colId);
      if (hasRelation) {
        triggerConfirm(
          '이 컬럼은 외래키(FK) 관계로 연결되어 있습니다. 컬럼을 삭제하면 다이어그램의 관계선도 함께 삭제됩니다. 정말로 삭제하시겠습니까?',
          () => {
            setEditingTable({
              ...editingTable,
              columns: editingTable.columns.filter(c => c.id !== colId)
            });
            setRelationships(prev => prev.filter(r => !(r.childTableId === editingTable.id && r.childColumnId === colId)));
            triggerAlert('컬럼 및 외래키 관계선이 제거되었습니다.', '삭제 완료', 'success');
          },
          '외래키 컬럼 삭제 확인'
        );
        return;
      }
    }
    setEditingTable({
      ...editingTable,
      columns: editingTable.columns.filter(c => c.id !== colId)
    });
  };

  // --- 관계선 삭제 기능 ---
  const handleDeleteRelation = (relId) => {
    triggerConfirm(
      '이 관계선 정보를 영구 삭제하시겠습니까? (자식 테이블의 FK 설정도 함께 제거됩니다)',
      () => {
        const rel = relationships.find(r => r.id === relId);
        if (rel) {
          // 자식 테이블의 FK 속성 제거 및 참조 해제
          setTables(prev => prev.map(t => {
            if (t.id === rel.childTableId) {
              return {
                ...t,
                columns: t.columns.filter(c => c.id !== rel.childColumnId) // 관계 해제 시 FK 컬럼 완전 삭제 처리
              };
            }
            return t;
          }));
        }
        setRelationships(prev => prev.filter(r => r.id !== relId));
      },
      '관계선 삭제'
    );
  };

  // --- 관계선 그리기 좌표 도출 ---
  const getColumnAnchorCoords = (tableId, colId) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return { x: 0, y: 0 };

    const colIndex = table.columns.findIndex(c => c.id === colId);
    const cardWidth = 260;
    const headerHeight = 52;
    const rowHeight = 30;

    const yVal = table.y + headerHeight + (colIndex === -1 ? 0 : colIndex) * rowHeight + (rowHeight / 2);

    return {
      left: { x: table.x, y: yVal },
      right: { x: table.x + cardWidth, y: yVal }
    };
  };

  // --- SQL DDL 자동 생성 엔진 ---
  const generateDDL = () => {
    let ddl = `-- Database Schema DDL for ${selectedDb}\n`;
    ddl += `-- Generated At: ${new Date().toLocaleString()}\n\n`;

    // 1. 테이블 생성
    tables.forEach(table => {
      ddl += `-- Table: ${table.physicalName} (${table.logicalName || '설명 없음'})\n`;
      if (selectedDb === 'SQLite') {
        ddl += `CREATE TABLE ${table.physicalName} (\n`;
      } else {
        ddl += `CREATE TABLE ${table.physicalName} (\n`;
      }

      const columnsSql = table.columns.map(col => {
        let line = `    ${col.physicalName} `;

        // 타입 + 사이즈
        line += col.dataType;
        const cleanedSize = col.size ? col.size.toString().replace(/['"]/g, '').trim() : '';
        if (cleanedSize && !col.dataType.includes('(')) {
          line += `(${cleanedSize})`;
        }

        // 제약조건
        if (col.notNull) line += ' NOT NULL';
        if (col.unique && !col.pk) line += ' UNIQUE';
        if (col.defaultValue) {
          const val = col.defaultValue.toString().trim();
          const valUpper = val.toUpperCase();
          const typeUpper = col.dataType.toUpperCase();
          
          const isStringType = typeUpper.startsWith('VARCHAR') || typeUpper.startsWith('CHAR') || typeUpper.includes('TEXT') || typeUpper.includes('CLOB');
          
          if (isStringType) {
            const cleanVal = val.replace(/^['"]|['"]$/g, '');
            const isKeyword = valUpper === 'NULL' || valUpper === 'CURRENT_TIMESTAMP' || valUpper.includes('NEXTVAL') || valUpper === 'TRUE' || valUpper === 'FALSE' || val.endsWith(')');
            if (isKeyword) {
              line += ` DEFAULT ${cleanVal}`;
            } else {
              line += ` DEFAULT '${cleanVal.replace(/'/g, "''")}'`;
            }
          } else {
            const cleanVal = val.replace(/^['"]|['"]$/g, '');
            line += ` DEFAULT ${cleanVal}`;
          }
        }

        // inline MYSQL comment
        if (selectedDb === 'MySQL' && col.comment) {
          line += ` COMMENT '${col.comment.replace(/'/g, "''")}'`;
        }

        return line;
      });

      // PK 설정
      const pkCols = table.columns.filter(c => c.pk).map(c => c.physicalName);
      if (pkCols.length > 0 && selectedDb !== 'SQLite') {
        columnsSql.push(`    CONSTRAINT pk_${table.physicalName} PRIMARY KEY (${pkCols.join(', ')})`);
      } else if (pkCols.length > 0 && selectedDb === 'SQLite') {
        // SQLite에서 PRIMARY KEY AUTOINCREMENT가 개별 컬럼에 인라인 선언되어 있다면 복합키 선언 스킵
        const hasInlineSqlitePk = table.columns.some(c => c.dataType.toUpperCase().includes('PRIMARY KEY'));
        if (!hasInlineSqlitePk) {
          columnsSql.push(`    PRIMARY KEY (${pkCols.join(', ')})`);
        }
      }

      // SQLite 전용 인라인 외래키 제약조건 설정
      if (selectedDb === 'SQLite') {
        const tableFks = table.columns.filter(c => c.fk && c.fkRef);
        tableFks.forEach(col => {
          const parentTable = tables.find(t => t.id === col.fkRef.tableId);
          const parentCol = parentTable?.columns.find(c => c.id === col.fkRef.columnId);
          if (parentTable && parentCol) {
            columnsSql.push(`    FOREIGN KEY (${col.physicalName}) REFERENCES ${parentTable.physicalName}(${parentCol.physicalName}) ON DELETE CASCADE`);
          }
        });
      }

      ddl += columnsSql.join(',\n') + '\n';

      // MySQL 테이블 코멘트
      if (selectedDb === 'MySQL') {
        ddl += `) COMMENT='${(table.logicalName || table.comment || '').replace(/'/g, "''")}';\n\n`;
      } else {
        ddl += `);\n\n`;
      }

      // Oracle & PostgreSQL 테이블/컬럼 코멘트문 생성
      if (selectedDb === 'PostgreSQL' || selectedDb === 'Oracle') {
        if (table.logicalName || table.comment) {
          ddl += `COMMENT ON TABLE ${table.physicalName} IS '${(table.logicalName || table.comment || '').replace(/'/g, "''")}';\n`;
        }
        table.columns.forEach(col => {
          if (col.logicalName || col.comment) {
            ddl += `COMMENT ON COLUMN ${table.physicalName}.${col.physicalName} IS '${(col.logicalName || col.comment || '').replace(/'/g, "''")}';\n`;
          }
        });
        ddl += '\n';
      }
    });

    // 2. 외래키 관계 제약조건 선언 (SQLite를 제외한 DBMS)
    if (selectedDb !== 'SQLite') {
      relationships.forEach(rel => {
        const parentTable = tables.find(t => t.id === rel.parentTableId);
        const parentCol = parentTable?.columns.find(c => c.id === rel.parentColumnId);
        const childTable = tables.find(t => t.id === rel.childTableId);
        const childCol = childTable?.columns.find(c => c.id === rel.childColumnId);

        if (parentTable && parentCol && childTable && childCol) {
          ddl += `-- Relationship: ${parentTable.physicalName} (${parentCol.physicalName}) -> ${childTable.physicalName} (${childCol.physicalName})\n`;
          ddl += `ALTER TABLE ${childTable.physicalName}\n`;
          ddl += `    ADD CONSTRAINT fk_${childTable.physicalName}_${childCol.physicalName}\n`;
          ddl += `    FOREIGN KEY (${childCol.physicalName}) REFERENCES ${parentTable.physicalName}(${parentCol.physicalName});\n\n`;
        }
      });
    }

    return ddl;
  };

  // --- 클립보드 복사 ---
  const handleCopyDdl = () => {
    navigator.clipboard.writeText(generateDDL()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // --- SQL 다운로드 ("추출하기") ---
  const handleDownloadDdl = () => {
    const content = generateDDL();
    const blob = new Blob([content], { type: 'text/sql;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${tables[0]?.physicalName || 'schema'}_structure.sql`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadJson = () => {
    const data = {
      dbType: selectedDb,
      canvasPan,
      canvasScale,
      tables,
      relationships,
      stickyNotes
    };
    const content = JSON.stringify(data, null, 2);
    const blob = new Blob([content], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${tables[0]?.physicalName || 'schema'}_structure.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUploadJson = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        
        if (!parsed || (typeof parsed !== 'object')) {
          throw new Error('유효한 JSON 객체가 아닙니다.');
        }

        let tablesToProcess = [];
        let relationshipsToProcess = [];
        let notesToProcess = [];
        let dbType = parsed.dbType || selectedDb;
        let pan = parsed.canvasPan || canvasPan;
        let scale = parsed.canvasScale || canvasScale;

        // 1. JSON 파일 포맷 파싱 및 대상 추출
        if (Array.isArray(parsed.tables)) {
          tablesToProcess = parsed.tables;
          relationshipsToProcess = Array.isArray(parsed.relationships) ? parsed.relationships : [];
          notesToProcess = Array.isArray(parsed.stickyNotes) ? parsed.stickyNotes : [];
        } else if (Array.isArray(parsed)) {
          tablesToProcess = parsed;
        } else if (parsed.tables && Array.isArray(parsed.tables)) {
          tablesToProcess = parsed.tables;
          relationshipsToProcess = Array.isArray(parsed.relationships) ? parsed.relationships : [];
          notesToProcess = Array.isArray(parsed.stickyNotes) ? parsed.stickyNotes : [];
          dbType = parsed.dbType || selectedDb;
        } else if (parsed && typeof parsed === 'object') {
          // 객체 키가 물리명인 딕셔너리 포맷 복구 (예: { tb_user: { columns: [...] } })
          const keys = Object.keys(parsed).filter(k => k !== 'dbType' && k !== 'canvasPan' && k !== 'canvasScale' && k !== 'tables' && k !== 'relationships' && k !== 'stickyNotes');
          if (keys.length > 0 && typeof parsed[keys[0]] === 'object') {
            tablesToProcess = keys.map(k => ({
              physicalName: k,
              logicalName: parsed[k].logicalName || parsed[k].comment || '',
              comment: parsed[k].comment || '',
              columns: parsed[k].columns || parsed[k].fields || []
            }));
          }
        }

        if (tablesToProcess.length === 0) {
          throw new Error('JSON 파일에 분석 가능한 테이블 데이터(tables)가 존재하지 않습니다.');
        }

        const validTables = [];
        const parseErrors = [];
        const validTablePhysicalNames = new Set();
        const validTableIds = new Set();

        // 2. 테이블 루프 검사 (개별 오류 수집하여 완전 포용적으로 수행)
        tablesToProcess.forEach((t, idx) => {
          const tableIndexText = `${idx + 1}번째 테이블`;
          const proposedName = t ? (t.physicalName || t.name) : null;
          const tableLabel = proposedName ? `테이블 '${proposedName}'` : tableIndexText;

          try {
            if (!t || typeof t !== 'object') {
              throw new Error('테이블 데이터가 유효한 객체가 아닙니다.');
            }

            if (!proposedName || !proposedName.toString().trim()) {
              throw new Error('테이블 물리명(physicalName 또는 name)이 비어있거나 누락되었습니다.');
            }

            const cleanPhysName = proposedName.toString().trim().replace(/[^a-zA-Z0-9_]/g, '');
            if (!cleanPhysName) {
              throw new Error(`테이블 물리명 '${proposedName}'은 올바르지 않은 물리명 기호로만 채워져 있습니다.`);
            }

            if (validTablePhysicalNames.has(cleanPhysName.toLowerCase())) {
              throw new Error(`중복된 테이블 물리명 '${cleanPhysName}'이 정의에 이미 추가되어 있습니다.`);
            }

            const columnsSrc = t.columns || t.fields;
            if (!columnsSrc) {
              throw new Error('컬럼 정의 목록(columns 또는 fields)이 누락되었습니다.');
            }
            if (!Array.isArray(columnsSrc)) {
              throw new Error('컬럼 정의가 배열 형태가 아닙니다.');
            }
            if (columnsSrc.length === 0) {
              throw new Error('테이블에 최소 한 개 이상의 컬럼이 정의되어야 합니다.');
            }

            const processedColumns = [];
            const colPhysicalNames = new Set();

            columnsSrc.forEach((c, cidx) => {
              const colIndexText = `${cidx + 1}번째 컬럼`;
              const colName = c ? (c.physicalName || c.name) : null;
              const colLabel = colName ? `컬럼 '${colName}'` : colIndexText;

              try {
                if (!c || typeof c !== 'object') {
                  throw new Error('컬럼 데이터가 유효한 객체가 아닙니다.');
                }
                if (!colName || !colName.toString().trim()) {
                  throw new Error(`${colIndexText}의 물리명이 비어있거나 누락되었습니다.`);
                }

                const cleanColName = colName.toString().trim().replace(/[^a-zA-Z0-9_]/g, '');
                if (!cleanColName) {
                  throw new Error(`컬럼 물리명 '${colName}'은 유효한 영어/숫자/_ 규칙을 위반했습니다.`);
                }

                if (colPhysicalNames.has(cleanColName.toLowerCase())) {
                  throw new Error(`테이블 내에 중복된 컬럼 물리명 '${cleanColName}'이 존재합니다.`);
                }

                const dataType = c.dataType || c.type;
                if (!dataType || !dataType.toString().trim()) {
                  throw new Error(`컬럼 '${cleanColName}'의 데이터 타입이 비어있거나 누락되었습니다.`);
                }

                const cleanSize = (sz) => {
                  if (sz === undefined || sz === null) return '';
                  const s = sz.toString().trim();
                  if (s === "''" || s === '""' || s === '""' || s === "''" || s === '') return '';
                  return s.replace(/['"]/g, '').trim();
                };

                colPhysicalNames.add(cleanColName.toLowerCase());
                let cleanDef = c.defaultValue || c.default || '';
                if (cleanDef) {
                  cleanDef = cleanDef.toString().trim().replace(/^['"]|['"]$/g, '');
                }
                processedColumns.push({
                  id: c.id || ('c_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4)),
                  physicalName: cleanColName,
                  logicalName: c.logicalName || c.comment || '',
                  dataType: dataType.toString().trim(),
                  size: cleanSize(c.size),
                  pk: !!(c.pk || c.primaryKey),
                  fk: !!(c.fk || c.foreignKey),
                  notNull: !!(c.notNull || c.nullable === false),
                  unique: !!c.unique,
                  defaultValue: cleanDef,
                  comment: c.comment || '',
                  fkRef: c.fkRef || null
                });
              } catch (colErr) {
                throw new Error(`${colLabel} 오류 - ${colErr.message}`);
              }
            });

            const tableId = t.id || ('t_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4));
            validTables.push({
              id: tableId,
              physicalName: cleanPhysName,
              logicalName: t.logicalName || t.comment || '',
              comment: t.comment || '',
              x: t.x !== undefined ? t.x : (100 + (validTables.length % 3) * 300),
              y: t.y !== undefined ? t.y : (100 + Math.floor(validTables.length / 3) * 280),
              columns: processedColumns
            });
            validTablePhysicalNames.add(cleanPhysName.toLowerCase());
            validTableIds.add(tableId);

          } catch (tabErr) {
            parseErrors.push(`[${tableLabel} 파싱 실패]: ${tabErr.message}`);
          }
        });

        // 3. 관계선 루프 검사
        const validRels = [];
        relationshipsToProcess.forEach((r, idx) => {
          try {
            if (!r || typeof r !== 'object') {
              throw new Error('관계선 명세가 유효한 객체가 아닙니다.');
            }

            // 물리명 기반 매핑 (AI 구조 등) 지원
            if (r.parentTable && r.childTable && r.parentColumn && r.childColumn) {
              const pTable = validTables.find(t => t.physicalName.toLowerCase() === r.parentTable.toLowerCase());
              const cTable = validTables.find(t => t.physicalName.toLowerCase() === r.childTable.toLowerCase());
              if (!pTable) throw new Error(`부모 테이블 '${r.parentTable}'이 복원에 성공하지 못했습니다.`);
              if (!cTable) throw new Error(`자식 테이블 '${r.childTable}'이 복원에 성공하지 못했습니다.`);

              const pCol = pTable.columns.find(c => c.physicalName.toLowerCase() === r.parentColumn.toLowerCase());
              const cCol = cTable.columns.find(c => c.physicalName.toLowerCase() === r.childColumn.toLowerCase());
              if (!pCol) throw new Error(`부모 테이블의 컬럼 '${r.parentColumn}'을 찾을 수 없습니다.`);
              if (!cCol) throw new Error(`자식 테이블의 컬럼 '${r.childColumn}'을 찾을 수 없습니다.`);

              validRels.push({
                id: r.id || ('r_' + Math.random().toString(36).substring(2, 9) + Date.now().toString().slice(-4)),
                parentTableId: pTable.id,
                parentColumnId: pCol.id,
                childTableId: cTable.id,
                childColumnId: cCol.id,
                type: r.type || '1:N'
              });
              return;
            }

            // ID 기반 명세인 경우
            if (!r.parentTableId || !r.childTableId || !r.parentColumnId || !r.childColumnId) {
              throw new Error('부모/자식 테이블 또는 컬럼 지시 정보가 불완전합니다.');
            }

            if (!validTableIds.has(r.parentTableId) || !validTableIds.has(r.childTableId)) {
              throw new Error('연결 대상 테이블이 오류 스킵되어 선을 연결할 수 없습니다.');
            }

            validRels.push({
              id: r.id || ('r_' + Date.now() + '_' + idx),
              parentTableId: r.parentTableId,
              parentColumnId: r.parentColumnId,
              childTableId: r.childTableId,
              childColumnId: r.childColumnId,
              type: r.type || '1:N'
            });
          } catch (relErr) {
            parseErrors.push(`[${idx + 1}번째 관계선 오류]: ${relErr.message}`);
          }
        });

        // 4. 스티커 메모 처리
        const validNotes = [];
        notesToProcess.forEach((n, idx) => {
          if (!n || typeof n !== 'object') return;
          validNotes.push({
            id: n.id || ('note_' + Date.now() + '_' + idx),
            x: n.x !== undefined ? n.x : 100,
            y: n.y !== undefined ? n.y : 100,
            w: n.w !== undefined ? n.w : 240,
            h: n.h !== undefined ? n.h : 160,
            color: n.color || '#fef08a',
            content: n.content || ''
          });
        });

        if (validTables.length === 0) {
          throw new Error(`파일에서 복원할 수 있는 유효한 테이블 구조가 하나도 존재하지 않습니다.\n\n[오류 기록]:\n${parseErrors.join('\n')}`);
        }

        // 5. 완료 알림 구성 및 적용 컨펌
        const successCount = validTables.length;
        const failCount = parseErrors.length;

        const applyImport = () => {
          const healed = healTablesAndRelationships(validTables, validRels);
          setTables(healed.tables);
          setRelationships(healed.relationships);
          setStickyNotes(validNotes);
          setSelectedDb(dbType);
          setCanvasPan(pan);
          setCanvasScale(scale);

          if (failCount > 0) {
            // 다이어그램 그린 뒤 오류가 있었음을 경고 모달로 노출
            const errMsg = `총 ${successCount}개의 테이블이 복원되었으나 아래 항목들은 오류로 스킵되었습니다.\n\n⚠️ [오류 로그 (총 ${failCount}건)]\n` + parseErrors.map(e => "- " + e).join("\n");
            setTimeout(() => {
              triggerAlert(
                errMsg,
                `JSON 복원 완료 (일부 스킵 발생)`,
                "warning"
              );
            }, 300);
          } else {
            triggerAlert('JSON 설계를 성공적으로 분석하여 복원했습니다!', '가져오기 성공', 'success');
          }
        };

        let confirmMsg = `업로드된 JSON 파일을 분석하여 다이어그램을 로드하시겠습니까? (기존 다이어그램이 교체됩니다.)\n\n* 분석 결과: 성공 ${successCount}건 / 오류 ${failCount}건`;
        if (failCount > 0) {
          confirmMsg += `\n* 오류가 발생한 테이블/관계선 목록은 스킵하고 성공한 목록만 캔버스에 그립니다.`;
        }

        triggerConfirm(
          confirmMsg,
          applyImport,
          'JSON 불러오기 확인'
        );
      } catch (err) {
        console.error(err);
        triggerAlert(`JSON 분석 실패: ${err.message || '올바르지 않은 JSON 파일입니다.'}`, '오류', 'danger');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // --- 테이블 검색 필터링 ---
  const filteredTables = tables.filter(table => {
    if (!tableSearchQuery.trim()) return true;
    const query = tableSearchQuery.toLowerCase();
    const pName = (table.physicalName || '').toLowerCase();
    const lName = (table.logicalName || '').toLowerCase();
    return pName.includes(query) || lName.includes(query);
  });

  return (
    <div className={`erd-container ${isSidebarOpen ? 'sidebar-active' : ''}`}>
      {/* 모바일 환경 사이드바 백드롭 오버레이 */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'show' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* 1. Left Sidebar: DBMS 설정, 테이블 목록, 범례 */}
      <div className={`erd-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="erd-sidebar-header d-flex flex-column gap-2">
          <div className="d-flex align-items-center gap-2 text-dark">
            <FaProjectDiagram style={{ color: '#6366f1' }} size={18} />
            <h5 className="mb-0 fw-bold" style={{ fontSize: '1rem', letterSpacing: '-0.02em' }}>뼈대도구 (ERD Builder)</h5>
          </div>
          <p className="text-muted mb-0" style={{ fontSize: '0.72rem' }}>UI 상에서 DB 설계를 진행하고 SQL을 빌드해보세요.</p>
        </div>

        <div className="erd-sidebar-content">
          {/* 클라우드 저장소 연동 패널 */}
          <div className="cloud-panel mb-4 p-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="small fw-bold text-slate-700 d-flex align-items-center gap-1">
                <FaDatabase style={{ color: isLoggedIn ? '#10b981' : '#64748b' }} />
                클라우드 저장소
              </span>
              {isLoggedIn ? (
                <span className="badge bg-success-subtle text-success border border-success-subtle text-truncate" style={{ fontSize: '0.65rem', maxWidth: '100px' }}>
                  {user?.nickname || '회원'}
                </span>
              ) : (
                <span
                  className="badge bg-secondary-subtle text-secondary border border-secondary-subtle cursor-pointer animate-pulse"
                  style={{ fontSize: '0.65rem' }}
                  onClick={() => window.dispatchEvent(new CustomEvent('wablog_session_expired'))}
                >
                  로그인 필요
                </span>
              )}
            </div>

            {isLoggedIn ? (
              <div className="d-flex flex-column gap-2 mt-2">
                {projectId && (
                  <div className="text-slate-600 mb-1" style={{ fontSize: '0.72rem' }}>
                    <div className="text-truncate fw-bold text-dark">{projectTitle}</div>
                    <div className="text-muted">버전: v{currentVersionNo}</div>
                  </div>
                )}
                <div className="d-grid gap-1">
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1.5"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => {
                        if (projectId) {
                          setSaveTitle(projectTitle);
                          setSaveDesc(projectDescription);
                        } else {
                          setSaveTitle('');
                          setSaveDesc('');
                        }
                        setShowCloudSaveModal(true);
                      }}
                    >
                      <FaSave size={12} /> 저장
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1.5"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => {
                        setShowCloudLoadModal(true);
                        fetchCloudProjects();
                      }}
                    >
                      <FaFolderOpen size={12} /> 불러오기
                    </Button>
                  </div>
                  <div className="d-flex gap-1">
                    {projectId && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1.5"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => {
                          setShowCloudHistoryModal(true);
                          fetchProjectHistory();
                        }}
                      >
                        <FaHistory size={12} /> 이력/버전
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1.5"
                      style={{ fontSize: '0.75rem' }}
                      onClick={handleNewDiagram}
                    >
                      새 설계
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-muted mb-2" style={{ fontSize: '0.68rem', lineHeight: '1.4' }}>
                  로그인하시면 클라우드에 설계를 안전하게 영구 보관하고 Git처럼 버전별로 되돌릴 수 있습니다.
                </p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="w-100 py-1"
                  style={{ fontSize: '0.75rem', borderColor: '#6366f1', color: '#6366f1' }}
                  onClick={() => window.dispatchEvent(new CustomEvent('wablog_session_expired'))}
                >
                  로그인하기
                </Button>
              </div>
            )}
          </div>

          {/* 샘플 ERD 선택 */}
          <div className="mb-4">
            <Form.Label className="text-secondary small fw-bold mb-1 d-flex align-items-center gap-1">
              <FaFolderOpen /> 샘플 ERD 불러오기
            </Form.Label>
            <Form.Select
              value=""
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  handleLoadSample(val);
                  e.target.value = ""; // reset value
                }
              }}
              className="bg-white border-light-subtle text-dark py-1.5"
              style={{ fontSize: '0.8rem' }}
            >
              <option value="" disabled>샘플을 선택하세요</option>
              <option value="basic">초급: 블로그 및 방명록 (테이블 4개)</option>
              <option value="ecommerce">중급: 쇼핑몰 결제 및 배송 (테이블 10개)</option>
              <option value="lms">고급: 대학 종합 학사 및 복지 (테이블 20개)</option>
            </Form.Select>
          </div>

          {/* DBMS 선택 */}
          <div className="mb-4">
            <Form.Label className="text-secondary small fw-bold mb-1 d-flex align-items-center gap-1">
              <FaDatabase /> 대상 DBMS
            </Form.Label>
            <Form.Select
              value={selectedDb}
              onChange={handleDbChange}
              className="bg-white border-light-subtle text-dark py-1.5"
              style={{ fontSize: '0.8rem' }}
            >
              {DBMS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </Form.Select>
          </div>

          {/* 뷰 모드 토글 */}
          <div className="mb-4">
            <Form.Label className="text-secondary small fw-bold mb-1 d-flex align-items-center gap-1">
              <FaColumns /> 표시 모드
            </Form.Label>
            <ButtonGroup size="sm" className="w-100">
              <Button
                variant={viewMode === 'logical' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('logical')}
                style={{ fontSize: '0.75rem' }}
              >
                논리명
              </Button>
              <Button
                variant={viewMode === 'physical' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('physical')}
                style={{ fontSize: '0.75rem' }}
              >
                물리명
              </Button>
              <Button
                variant={viewMode === 'both' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('both')}
                style={{ fontSize: '0.75rem' }}
              >
                모두
              </Button>
            </ButtonGroup>
          </div>

          <hr className="border-light-subtle my-3" />

          {/* 테이블 생성 버튼 */}
          <Button
            variant="indigo"
            onClick={handleAddTable}
            className="w-100 d-flex align-items-center justify-content-center gap-2 mb-3 py-2 text-white border-0 shadow-sm"
            style={{ fontSize: '0.82rem', backgroundColor: '#6366f1' }}
          >
            <FaPlus /> 테이블 추가
          </Button>

          {/* 테이블 목록 */}
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
              <div className="text-secondary small fw-bold text-nowrap">
                테이블 목록 ({tables.length})
              </div>
              <div className="position-relative flex-grow-1" style={{ maxWidth: '140px' }}>
                <input
                  type="text"
                  className="form-control form-control-sm py-1"
                  placeholder="테이블 검색"
                  value={tableSearchQuery}
                  onChange={e => setTableSearchQuery(e.target.value)}
                  style={{ fontSize: '0.72rem', paddingRight: '22px', height: '24px' }}
                />
                {tableSearchQuery && (
                  <button
                    type="button"
                    className="btn btn-link p-0 position-absolute end-0 top-50 translate-middle-y me-2 text-secondary"
                    onClick={() => setTableSearchQuery('')}
                    style={{ border: 'none', background: 'none', fontSize: '0.85rem', textDecoration: 'none', color: '#6c757d', boxShadow: 'none' }}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>

            {tables.length === 0 ? (
              <p className="text-muted small text-center py-3">추가된 테이블이 없습니다.</p>
            ) : filteredTables.length === 0 ? (
              <p className="text-muted small text-center py-3">검색 결과가 없습니다.</p>
            ) : (
              filteredTables.map(table => (
                <div
                  key={table.id}
                  className={`table-list-item ${selectedTableId === table.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedTableId(table.id);
                    // 클릭 시 테이블 위치로 캔버스 패닝 포커스
                    setCanvasPan({
                      x: -table.x * canvasScale + 120,
                      y: -table.y * canvasScale + 100
                    });

                    // 깜빡임 하이라이트 애니메이션 트리거
                    setHighlightedTableId(table.id);
                    setTimeout(() => {
                      setHighlightedTableId(null);
                    }, 1200);
                  }}
                >
                  <div className="text-truncate" style={{ maxWidth: '180px' }}>
                    <strong className="text-dark">{table.physicalName}</strong>
                    <div className="text-secondary" style={{ fontSize: '0.7rem' }}>{table.logicalName || '(논리명 없음)'}</div>
                  </div>
                  <div className="d-flex gap-1">
                    <Button
                      variant="link"
                      className="p-0 text-secondary hover:text-dark"
                      onClick={(e) => handleOpenEditModal(table, e)}
                    >
                      <FaEdit size={12} />
                    </Button>
                    <Button
                      variant="link"
                      className="p-0 text-danger"
                      onClick={(e) => handleDeleteTable(table.id, e)}
                    >
                      <FaTrash size={12} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 사용 방법 안내 */}
          <div className="mt-4 p-3 bg-light rounded border border-light-subtle text-secondary" style={{ fontSize: '0.7rem', lineHeight: '1.4' }}>
            <strong>💡 UX 및 기호 설명 팁:</strong>
            <ul className="ps-3 mb-0 mt-1">
              <li>테이블 헤더를 드래깅하여 이동시킬 수 있습니다.</li>
              <li>컬럼 우측 <b>● 커넥터</b>를 드래그 앤 드롭하면 <b>FK 관계</b>가 자동 생성됩니다.</li>
              <li><b>실선</b>: 식별 관계 (FK 컬럼이 PK인 경우)</li>
              <li><b>점선</b>: 비식별 관계 (FK 컬럼이 일반 속성인 경우)</li>
              <li><b>카디널리티</b>: <b>||</b>는 1개, <b>|∈</b>는 1개 이상(NOT NULL인 경우), <b>O∈</b>는 0개 이상(NULL 허용인 경우)을 나타냅니다.</li>
              <li>관계선(곡선) 클릭 시 관계가 삭제됩니다.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 2. Main Workspace: Canvas & SVG Connections Layer */}
      <div
        ref={canvasContainerRef}
        className="erd-canvas-container"
        onMouseDown={handleCanvasMouseDown}
        onTouchStart={handleCanvasTouchStart}
        onContextMenu={handleContextMenu}
      >
        {/* 사이드바 토글 버튼 (접고 펴기) */}
        <button
          className="sidebar-toggle-btn"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          title="사이드바 토글"
        >
          <FaBars />
        </button>

        {/* 드래그 상태가 'connect' 일 때 안내 메시지 노출 */}
        {dragState?.type === 'connect' && (
          <div className="drag-guide-msg">
            자식 테이블 박스 영역 안에 드롭하여 FK(외래키) 관계를 완성하세요!
          </div>
        )}

        {/* Floating Toolbar */}
        <div className="canvas-toolbar">
          <button className="canvas-toolbar-btn" onClick={handleAddStickyNote} title="스티커 메모 추가"><FaStickyNote /></button>
          <button className="canvas-toolbar-btn" onClick={() => handleZoom(0.1)} title="줌 인"><FaSearchPlus /></button>
          <button className="canvas-toolbar-btn" onClick={() => handleZoom(-0.1)} title="줌 아웃"><FaSearchMinus /></button>
          <button className="canvas-toolbar-btn" onClick={resetZoom} title="줌 리셋 및 센터"><FaRedo /></button>
        </div>

        {/* 캔버스 드로잉 본체 */}
        <div
          className="erd-canvas"
          style={{
            transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasScale})`
          }}
        >
          {/* Marquee selection box */}
          {dragState && dragState.type === 'select' && (
            <div
              className="erd-selection-marquee"
              style={{
                left: Math.min(dragState.canvasStartX, dragState.canvasCurX),
                top: Math.min(dragState.canvasStartY, dragState.canvasCurY),
                width: Math.abs(dragState.canvasStartX - dragState.canvasCurX),
                height: Math.abs(dragState.canvasStartY - dragState.canvasCurY)
              }}
            />
          )}

          {/* SVG 커넥션 레이어 */}
          <svg className="erd-svg-overlay">
            {/* 마커 등록: 화살표나 카디널리티 표현용 */}
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#6366f1" />
              </marker>
              <marker id="double-bar" viewBox="0 0 10 10" refX="2" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <line x1="3" y1="1" x2="3" y2="9" stroke="#6366f1" strokeWidth="1.5" />
                <line x1="7" y1="1" x2="7" y2="9" stroke="#6366f1" strokeWidth="1.5" />
              </marker>
              <marker id="many-required" viewBox="0 0 16 10" refX="15" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                <path d="M 15 2 L 6 5 L 15 8" fill="none" stroke="#6366f1" strokeWidth="1.5" />
                <line x1="0" y1="5" x2="6" y2="5" stroke="#6366f1" strokeWidth="1.5" />
                <line x1="4" y1="1" x2="4" y2="9" stroke="#6366f1" strokeWidth="1.5" />
              </marker>
              <marker id="many-optional" viewBox="0 0 16 10" refX="15" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                <path d="M 15 2 L 8 5 L 15 8" fill="none" stroke="#6366f1" strokeWidth="1.5" />
                <line x1="0" y1="5" x2="8" y2="5" stroke="#6366f1" strokeWidth="1.5" />
                <circle cx="4" cy="5" r="2" fill="#ffffff" stroke="#6366f1" strokeWidth="1.5" />
              </marker>
            </defs>

            {/* 현재 드래그 중인 임시 관계선 그리기 */}
            {dragState?.type === 'connect' && (
              <line
                x1={dragState.startX}
                y1={dragState.startY}
                x2={dragState.curX}
                y2={dragState.curY}
                stroke="#38bdf8"
                strokeWidth="2.5"
                strokeDasharray="4 4"
              />
            )}

            {/* 테이블 간 저장된 관계선 그리기 */}
            {relationships.map(rel => {
              const pTable = tables.find(t => t.id === rel.parentTableId);
              const cTable = tables.find(t => t.id === rel.childTableId);

              if (!pTable || !cTable) return null;

              // 외래키 컬럼 속성을 조사하여 카디널리티 및 식별/비식별 자동 판별
              const childCol = cTable.columns.find(c => c.id === rel.childColumnId);
              const isIdentifying = childCol ? childCol.pk : false;
              const isRequired = childCol ? childCol.notNull : true;

              // 좌우 관계 배치에 따라 앵커 포인트를 좌측 또는 우측 선택
              const isParentLeft = pTable.x + 130 < cTable.x + 130;
              const pAnchor = isParentLeft ? 'right' : 'left';
              const cAnchor = isParentLeft ? 'left' : 'right';

              const startCoords = getColumnAnchorCoords(rel.parentTableId, rel.parentColumnId)[pAnchor];
              const endCoords = getColumnAnchorCoords(rel.childTableId, rel.childColumnId)[cAnchor];

              // 예쁜 3차 베지어 곡선 제어점 도출
              const dx = Math.min(180, Math.max(50, Math.abs(endCoords.x - startCoords.x) / 1.5));
              const ctrlX1 = pAnchor === 'right' ? startCoords.x + dx : startCoords.x - dx;
              const ctrlX2 = cAnchor === 'right' ? endCoords.x + dx : endCoords.x - dx;

              const pathData = `M ${startCoords.x} ${startCoords.y} C ${ctrlX1} ${startCoords.y}, ${ctrlX2} ${endCoords.y}, ${endCoords.x} ${endCoords.y}`;

              return (
                <g key={rel.id}>
                  {/* 외곽 보조선 (호버 터치 면적 넓히기용) */}
                  <path
                    d={pathData}
                    fill="none"
                    stroke="transparent"
                    strokeWidth="10"
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                    onClick={() => handleDeleteRelation(rel.id)}
                  />
                  {/* 메인 선 (식별/비식별에 따른 실선/점선 동적 드로잉 및 까마귀발 카디널리티 매핑) */}
                  <path
                    d={pathData}
                    className="erd-relation-path"
                    strokeDasharray={isIdentifying ? "none" : "6 4"}
                    markerStart="url(#double-bar)"
                    markerEnd={isRequired ? "url(#many-required)" : "url(#many-optional)"}
                    onClick={() => handleDeleteRelation(rel.id)}
                    title="클릭하여 관계선 끊기"
                  />
                </g>
              );
            })}
          </svg>

          {/* 테이블 노드 카드들 */}
          {tables.map(table => {
            const isSelected = selectedTableIds.includes(table.id) || selectedTableId === table.id;
            return (
              <div
                key={table.id}
                className={`erd-table-card ${isSelected ? 'selected' : ''} ${highlightedTableId === table.id ? 'blink-highlight' : ''}`}
                style={{
                  left: table.x,
                  top: table.y
                }}
                onDoubleClick={(e) => handleOpenEditModal(table, e)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (e.target.closest('.erd-table-header')) return;
                  setSelectedTableId(table.id);
                  if (e.ctrlKey || e.metaKey || e.shiftKey) {
                    if (selectedTableIds.includes(table.id)) {
                      const next = selectedTableIds.filter(id => id !== table.id);
                      setSelectedTableIds(next);
                      setSelectedTableId(next[0] || null);
                    } else {
                      setSelectedTableIds([...selectedTableIds, table.id]);
                    }
                  } else {
                    setSelectedTableIds([table.id]);
                    setSelectedNoteIds([]);
                  }
                }}
              >
                {/* 테이블 카드 헤더 */}
                <div
                  className="erd-table-header"
                  onMouseDown={(e) => handleTableHeaderMouseDown(table.id, e)}
                  onTouchStart={(e) => handleTableHeaderTouchStart(table.id, e)}
                >
                  <div className="table-title">
                    <FaProjectDiagram size={13} className="text-secondary" />
                    {viewMode === 'logical' ? (table.logicalName || table.physicalName) : table.physicalName}
                  </div>
                  {(viewMode === 'both' || (viewMode === 'physical' && table.logicalName)) && (
                    <div className="table-subtitle">
                      {viewMode === 'both' ? (table.logicalName || '(논리명 없음)') : table.physicalName}
                    </div>
                  )}
                </div>

                {/* 테이블 카드 바디 (컬럼 행 리스트) */}
                <div className="erd-table-body">
                  {table.columns.map((col, idx) => {
                    const showName = viewMode === 'logical' ? (col.logicalName || col.physicalName) : col.physicalName;
                    const showSubName = viewMode === 'both' ? (col.logicalName || col.physicalName) : null;

                    const isDraggingCol = draggedColInfo && draggedColInfo.tableId === table.id && draggedColInfo.colId === col.id;

                    return (
                      <div
                        key={col.id}
                        className={`erd-column-row ${isDraggingCol ? 'dragging' : ''}`}
                        draggable={true}
                        onDragStart={(e) => handleColDragStart(e, table.id, col.id, idx)}
                        onDragOver={(e) => handleColDragOver(e, table.id, idx)}
                        onDrop={(e) => handleColDrop(e, table.id, idx)}
                        onDragEnd={handleColDragEnd}
                      >
                        <div className="erd-column-name-area">
                          {col.pk && <span className="key-icon pk" title="기본키 (PK)"><FaKey /></span>}
                          {col.fk && <span className="key-icon fk" title={`외래키 (FK) -> ${col.fkRef?.tableId ? (tables.find(t => t.id === col.fkRef.tableId)?.physicalName || '') : ''}`}><FaLink /></span>}
                          <span
                            className="text-dark"
                            style={{ opacity: col.pk ? 1 : 0.85, fontWeight: col.pk ? '700' : 'normal' }}
                          >
                            {showName}
                            {showSubName && (
                              <span className="text-secondary ms-1" style={{ fontSize: '0.68rem' }}>
                                ({showSubName})
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <span className="erd-column-type">
                            {col.dataType}
                            {col.size && col.size.toString().replace(/['"]/g, '').trim() && `(${col.size.toString().replace(/['"]/g, '').trim()})`}
                          </span>

                          {/* 드래그 관계 생성용 우측 도트 커넥터 (PK에만 연결권한을 줌) */}
                          {col.pk && (
                            <div
                              className="connector-handle"
                              onMouseDown={(e) => handleConnectorMouseDown(table.id, col.id, e)}
                              onTouchStart={(e) => handleConnectorTouchStart(table.id, col.id, e)}
                              title="드래그하여 다른 테이블로 외래키(FK) 선을 연결하세요"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* 스티커 메모 노드 카드들 */}
          {stickyNotes.map(note => {
            const isEditing = editingNoteId === note.id;
            const isNoteSelected = selectedNoteIds.includes(note.id);
            return (
              <div
                key={note.id}
                className={`erd-sticky-note animate-fade-in ${isNoteSelected ? 'selected' : ''}`}
                style={{
                  left: note.x,
                  top: note.y,
                  width: note.w,
                  height: note.h,
                  backgroundColor: note.color || '#fef08a'
                }}
                onMouseDown={(e) => handleNoteMouseDown(note.id, e)}
                onTouchStart={(e) => handleNoteTouchStart(note.id, e)}
                onDoubleClick={(e) => { e.stopPropagation(); handleNoteDoubleClick(note.id); }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {/* 메모 컨트롤 헤더 */}
                <div className="note-header d-flex align-items-center justify-content-between">
                  <span className="small fw-bold text-dark" style={{ fontSize: '0.65rem', opacity: 0.65 }}>📌 스티커 메모</span>
                  <div className="d-flex align-items-center gap-2">
                    {isEditing ? (
                      <Button
                        size="sm"
                        variant="link"
                        className="text-success p-0 m-0 border-0 note-action-btn"
                        onClick={() => setEditingNoteId(null)}
                        title="편집 완료"
                      >
                        <FaCheck size={16} />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="link"
                        className="text-secondary p-0 m-0 border-0 note-action-btn"
                        onClick={() => setEditingNoteId(note.id)}
                        title="메모 수정"
                      >
                        <FaEdit size={16} />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="link"
                      className="text-danger p-0 m-0 border-0 note-action-btn"
                      onClick={(e) => handleDeleteNote(note.id, e)}
                      title="메모 삭제"
                    >
                      <FaTrash size={16} />
                    </Button>
                  </div>
                </div>

                {/* 메모 본문 */}
                <div className="note-body">
                  {isEditing ? (
                    <div className="d-flex flex-column h-100 gap-2">
                      <textarea
                        className="note-textarea"
                        value={note.content}
                        onChange={(e) => handleUpdateNoteContent(note.id, e.target.value)}
                        placeholder="마크다운 양식으로 내용을 기입하세요..."
                        autoFocus
                      />
                      <div className="note-colors d-flex gap-1 mb-1">
                        {['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa'].map(c => (
                          <div
                            key={c}
                            className={`color-dot ${note.color === c ? 'active' : ''}`}
                            style={{ backgroundColor: c }}
                            onClick={() => handleUpdateNoteColor(note.id, c)}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="note-content-preview h-100"
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(note.content) }}
                    />
                  )}
                </div>

                {/* 리사이즈 핸들 */}
                <div
                  className="note-resize-handle"
                  onMouseDown={(e) => handleNoteResizeMouseDown(note.id, e)}
                  onTouchStart={(e) => handleNoteResizeTouchStart(note.id, e)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Bottom SQL Script Drawer (DDL 실시간 프리뷰 및 AI 설계 어시스턴트) */}
      <div className={`erd-ddl-drawer ${isDdlExpanded ? '' : 'collapsed'}`}>
        <div
          className="erd-ddl-header"
          onClick={() => setIsDdlExpanded(!isDdlExpanded)}
        >
          <span className="small text-dark fw-bold d-flex align-items-center gap-2">
            <FaSearchPlus size={13} />
            실시간 DDL 추출 및 AI 설계 어시스턴트 ({selectedDb} 구문 기준)
          </span>
          <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="outline-secondary" className="py-0.5 text-dark" onClick={handleCopyDdl} style={{ fontSize: '0.72rem' }}>
              {copied ? <><FaCheck /> 복사됨</> : <><FaCopy /> 클립보드 복사</>}
            </Button>
            <label className="btn btn-sm btn-outline-info py-0.5 text-dark mb-0 d-flex align-items-center gap-1 animate-hover" style={{ fontSize: '0.72rem', cursor: 'pointer' }}>
              <FaFolderOpen size={12} /> JSON 가져오기
              <input
                type="file"
                accept=".json"
                onChange={handleUploadJson}
                style={{ display: 'none' }}
              />
            </label>
            <Button size="sm" variant="outline-primary" className="py-0.5 text-dark d-flex align-items-center gap-1 animate-hover" onClick={handleDownloadJson} style={{ fontSize: '0.72rem' }}>
              <FaDownload size={12} /> JSON 내보내기
            </Button>
            <Button size="sm" variant="success" className="py-0.5 text-white" onClick={handleDownloadDdl} style={{ fontSize: '0.72rem' }}>
              <FaDownload /> 추출하기 (.sql 다운로드)
            </Button>
            <Button size="sm" variant="link" className="text-secondary p-0 ms-2" onClick={() => setIsDdlExpanded(!isDdlExpanded)}>
              {isDdlExpanded ? <FaTimes /> : <FaPlus />}
            </Button>
          </div>
        </div>
        <div className="erd-ddl-body">
          {/* 좌측: DDL 뷰어 */}
          <div className="erd-ddl-pane">
            <pre className="sql-code-block">
              {generateDDL()}
            </pre>
          </div>

          {/* 우측: AI 설계 채팅 */}
          <div className="erd-ai-chat-pane">
            <div className="erd-ai-chat-messages" ref={chatMessagesRef}>
              {aiMessages.map((msg, idx) => (
                <div key={idx} className={`erd-ai-message ${msg.type}`}>
                  {msg.text}
                </div>
              ))}
              {aiLoading && (
                <div className="erd-ai-chat-loading">
                  <span className="spinner-border spinner-border-sm text-indigo me-1" role="status" aria-hidden="true" style={{ width: '12px', height: '12px' }}></span>
                  AI가 테이블을 설계 중입니다... ({processingTime.toFixed(1)}s 경과)
                </div>
              )}
            </div>
            <form className="erd-ai-chat-input-wrapper" onSubmit={handleSendAiMessage}>
              <input
                type="text"
                className="erd-ai-chat-input"
                placeholder="예: 해시태그 기능이 포함된 자유게시판 설계해줘"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                disabled={aiLoading}
              />
              <button
                type="submit"
                className="erd-ai-chat-send-btn animate-hover"
                disabled={aiLoading || !aiInput.trim()}
                style={{ backgroundColor: '#6366f1' }}
              >
                전송
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* 4. Table & Column Editor Modal */}
      {showTableModal && editingTable && (
        <Modal
          show={showTableModal}
          onHide={() => setShowTableModal(false)}
          size="xl"
          dialogClassName="modal-light-theme"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold text-dark">테이블 구조 설계기 ({editingTable.physicalName})</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* 테이블 정보 */}
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-secondary fw-bold">테이블 물리명 (영어/숫자)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingTable.physicalName}
                    onChange={e => setEditingTable({ ...editingTable, physicalName: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                    placeholder="e.g. tb_order"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-secondary fw-bold">테이블 논리명 (한글명)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingTable.logicalName}
                    onChange={e => setEditingTable({ ...editingTable, logicalName: e.target.value })}
                    placeholder="e.g. 주문 내역 테이블"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small text-secondary fw-bold">테이블 주석/설명</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingTable.comment || ''}
                    onChange={e => setEditingTable({ ...editingTable, comment: e.target.value })}
                    placeholder="이 테이블에 대한 설명을 입력해 주세요"
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr className="border-secondary" />

            {/* 컬럼 설계 테이블 */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="small text-secondary fw-bold">컬럼 목록 정의</div>
              <Button size="sm" variant="primary" onClick={handleModalAddColumn} style={{ fontSize: '0.75rem' }}>
                <FaPlus size={11} /> 컬럼 추가
              </Button>
            </div>

            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <Table responsive borderless className="column-edit-table text-dark bg-white" style={{ minWidth: '950px' }}>
                <thead>
                  <tr className="border-bottom border-secondary text-secondary">
                    <th style={{ width: '160px' }}>물리명</th>
                    <th style={{ width: '150px' }}>논리명</th>
                    <th style={{ width: '170px' }}>데이터 타입</th>
                    <th style={{ width: '80px' }}>길이</th>
                    <th className="text-center" style={{ width: '45px' }}>PK</th>
                    <th className="text-center" style={{ width: '50px' }}>Not Null</th>
                    <th className="text-center" style={{ width: '45px' }}>UQ</th>
                    <th style={{ width: '110px' }}>기본값</th>
                    <th style={{ width: '180px' }}>설명</th>
                    <th className="text-center" style={{ width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {editingTable.columns.map((col, idx) => (
                    <tr key={col.id} className="align-middle">
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={col.physicalName}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].physicalName = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                          className="bg-secondary-subtle border-0"
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={col.logicalName}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].logicalName = e.target.value;
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                          className="bg-secondary-subtle border-0"
                        />
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Form.Control
                            type="text"
                            value={col.dataType}
                            onChange={e => {
                              const newCols = [...editingTable.columns];
                              const newType = e.target.value;
                              newCols[idx].dataType = newType;
                              if (!doesTypeSupportSize(newType)) {
                                newCols[idx].size = '';
                              }
                              setEditingTable({ ...editingTable, columns: newCols });
                            }}
                            className="bg-secondary-subtle border-0 text-truncate"
                            disabled={col.fk}
                          />
                          <Form.Select
                            onChange={e => {
                              const newCols = [...editingTable.columns];
                              const newType = e.target.value;
                              newCols[idx].dataType = newType;
                              if (!doesTypeSupportSize(newType)) {
                                newCols[idx].size = '';
                              }
                              setEditingTable({ ...editingTable, columns: newCols });
                            }}
                            className="bg-dark text-white border-secondary border-0"
                            style={{ maxWidth: '40px' }}
                            disabled={col.fk}
                            value=""
                          >
                            <option value="" disabled></option>
                            {RECOMMENDED_TYPES[selectedDb]?.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </Form.Select>
                        </InputGroup>
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={doesTypeSupportSize(col.dataType) ? (col.size || '') : ''}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].size = e.target.value;
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                          className="bg-secondary-subtle border-0"
                          placeholder={doesTypeSupportSize(col.dataType) ? "e.g. 50" : "-"}
                          disabled={col.fk || !doesTypeSupportSize(col.dataType)}
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={col.pk}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].pk = e.target.checked;
                            if (e.target.checked) {
                              newCols[idx].notNull = true; // PK는 NOT NULL 기본 적용
                            }
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={col.notNull}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].notNull = e.target.checked;
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                          disabled={col.pk} // PK이면 NOT NULL 강제
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={col.unique}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].unique = e.target.checked;
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                          disabled={col.pk}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={col.defaultValue || ''}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].defaultValue = e.target.value;
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                          className="bg-secondary-subtle border-0"
                          placeholder="e.g. NULL"
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={col.comment || ''}
                          onChange={e => {
                            const newCols = [...editingTable.columns];
                            newCols[idx].comment = e.target.value;
                            setEditingTable({ ...editingTable, columns: newCols });
                          }}
                          className="bg-secondary-subtle border-0"
                          placeholder="설명"
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="link"
                          className="text-danger p-0 border-0"
                          onClick={() => handleModalDeleteColumn(col.id)}
                          title={col.fk ? "외래키(FK) 컬럼 삭제 (관계선도 제거됩니다)" : "삭제"}
                        >
                          <FaTrash size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <div>
              <Button variant="outline-warning" onClick={handleValidateTable} className="small py-1.5 px-3">
                🔍 설계 유효성 검사
              </Button>
            </div>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={() => setShowTableModal(false)} className="small py-1.5 px-3">
                취소
              </Button>
              <Button variant="indigo" onClick={handleSaveTableModal} className="small py-1.5 px-3 text-white" style={{ backgroundColor: '#6366f1' }}>
                변경사항 저장
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      {/* 5. Cloud Save Modal */}
      {showCloudSaveModal && (
        <Modal show={showCloudSaveModal} onHide={() => setShowCloudSaveModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold text-dark">
              {projectId ? '새 버전 저장 (Commit)' : '클라우드 새 설계 저장'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCloudSaveSubmit}>
            <Modal.Body>
              {!projectId ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-secondary fw-bold">다이어그램 제목</Form.Label>
                    <Form.Control
                      type="text"
                      value={saveTitle}
                      onChange={e => setSaveTitle(e.target.value)}
                      placeholder="예: 서비스 1차 뼈대 설계"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-secondary fw-bold">다이어그램 설명 (선택)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={saveDesc}
                      onChange={e => setSaveDesc(e.target.value)}
                      placeholder="다이어그램 목적에 대한 간단한 설명을 입력하세요."
                    />
                  </Form.Group>
                </>
              ) : (
                <div className="mb-3 text-slate-700" style={{ fontSize: '0.8rem' }}>
                  현재 프로젝트 <b>{projectTitle}</b>의 신규 버전을 클라우드 데이터베이스에 추가 저장합니다.
                </div>
              )}
              <Form.Group className="mb-3">
                <Form.Label className="small text-secondary fw-bold">변경 내용 설명 (저장 메시지)</Form.Label>
                <Form.Control
                  type="text"
                  value={saveCommitMsg}
                  onChange={e => setSaveCommitMsg(e.target.value)}
                  placeholder={projectId ? "예: 댓글 테이블 컬럼 추가" : "예: 최초 설계 저장"}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowCloudSaveModal(false)} className="small py-1.5 px-3">
                취소
              </Button>
              <Button type="submit" variant="indigo" className="small py-1.5 px-3 text-white" style={{ backgroundColor: '#6366f1' }}>
                저장하기
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {/* 6. Cloud Load Modal */}
      {showCloudLoadModal && (
        <Modal show={showCloudLoadModal} onHide={() => setShowCloudLoadModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold text-dark">클라우드 다이어그램 불러오기</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '450px', overflowY: 'auto' }}>
            {loadingProjects ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                <span className="text-muted small">목록을 불러오는 중...</span>
              </div>
            ) : cloudProjects.length === 0 ? (
              <p className="text-muted small text-center py-4">클라우드 저장소에 보관된 다이어그램이 존재하지 않습니다.</p>
            ) : (
              <Table hover responsive size="sm" className="align-middle" style={{ fontSize: '0.8rem' }}>
                <thead className="table-light">
                  <tr>
                    <th>프로젝트 제목</th>
                    <th>설명</th>
                    <th>버전 수</th>
                    <th>마지막 수정일</th>
                    <th className="text-center" style={{ width: '120px' }}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {cloudProjects.map(proj => (
                    <tr
                      key={proj.project_id}
                      className="cursor-pointer"
                      onClick={() => handleLoadProject(proj.project_id)}
                    >
                      <td><strong className="text-slate-800">{proj.title}</strong></td>
                      <td className="text-muted text-truncate" style={{ maxWidth: '200px' }}>
                        {proj.description || '-'}
                      </td>
                      <td>v{proj.latest_version}</td>
                      <td>{new Date(proj.updated_at).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="link"
                          className="text-danger p-0"
                          onClick={(e) => handleCloudDelete(proj.project_id, e)}
                        >
                          <FaTrash size={12} className="me-1" /> 삭제
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCloudLoadModal(false)} className="small py-1.5 px-3">
              닫기
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* 7. Cloud Version History Modal */}
      {showCloudHistoryModal && (
        <Modal show={showCloudHistoryModal} onHide={() => setShowCloudHistoryModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold text-dark">
              이력 및 버전 관리 (v{currentVersionNo}) - {projectTitle}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loadingHistory ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                <span className="text-muted small">이력 로그를 조회하는 중...</span>
              </div>
            ) : historyList.length === 0 ? (
              <p className="text-muted small text-center py-4">버전 이력이 존재하지 않습니다.</p>
            ) : (
              <Table hover responsive size="sm" className="align-middle" style={{ fontSize: '0.8rem' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '80px' }}>버전</th>
                    <th>변경 내역 (Commit Message)</th>
                    <th>저장일시</th>
                    <th>작성자</th>
                    <th className="text-center" style={{ width: '120px' }}>복원</th>
                  </tr>
                </thead>
                <tbody>
                  {historyList.map(hist => (
                    <tr key={hist.version_id}>
                      <td><span className="badge bg-indigo text-white px-2 py-1" style={{ backgroundColor: '#6366f1' }}>v{hist.version_no}</span></td>
                      <td className="fw-medium text-dark">{hist.save_message || '설명 없음'}</td>
                      <td className="text-muted">{new Date(hist.created_at).toLocaleString('ko-KR')}</td>
                      <td>{hist.creator_nickname || '회원'}</td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="py-0.5 px-2"
                          style={{ fontSize: '0.72rem' }}
                          onClick={() => handleLoadVersion(hist.version_no)}
                          disabled={currentVersionNo === hist.version_no}
                        >
                          {currentVersionNo === hist.version_no ? '현재 버전' : '이 버전으로 롤백'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCloudHistoryModal(false)} className="small py-1.5 px-3">
              닫기
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* 5. Custom Alert Modal */}
      <Modal
        show={alertModal.show}
        onHide={() => setAlertModal(prev => ({ ...prev, show: false }))}
        centered
        dialogClassName="custom-alert-modal"
      >
        <Modal.Body className="p-4 text-center">
          <div className="mb-3">
            {alertModal.variant === 'success' && <FaCheck size={48} className="text-success" />}
            {alertModal.variant === 'danger' && <FaTimes size={48} className="text-danger" />}
            {alertModal.variant === 'warning' && <FaExclamationTriangle size={48} className="text-warning" />}
            {alertModal.variant === 'info' && <FaInfoCircle size={48} className="text-info" />}
          </div>
          <h5 className="fw-bold text-dark mb-2">{alertModal.title}</h5>
          <p className="text-secondary mb-4 small" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{alertModal.message}</p>
          <div className="d-flex justify-content-center">
            <Button
              variant="indigo"
              onClick={() => setAlertModal(prev => ({ ...prev, show: false }))}
              className="px-4 py-2 text-white border-0 shadow-sm"
              style={{ backgroundColor: '#6366f1', fontSize: '0.85rem' }}
            >
              확인
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* 6. Custom Confirm Modal */}
      <Modal
        show={confirmModal.show}
        onHide={confirmModal.onCancel}
        centered
        dialogClassName="custom-confirm-modal"
      >
        <Modal.Body className="p-4 text-center">
          <div className="mb-3 text-warning">
            <FaExclamationTriangle size={48} />
          </div>
          <h5 className="fw-bold text-dark mb-2">{confirmModal.title}</h5>
          <p className="text-secondary mb-4 small" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{confirmModal.message}</p>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="light"
              onClick={confirmModal.onCancel}
              className="px-4 py-2 border shadow-sm text-dark bg-light"
              style={{ fontSize: '0.85rem' }}
            >
              취소
            </Button>
            <Button
              variant="indigo"
              onClick={confirmModal.onConfirm}
              className="px-4 py-2 text-white border-0 shadow-sm"
              style={{ backgroundColor: '#6366f1', fontSize: '0.85rem' }}
            >
              확인
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* 7. AI 설계 제안 미리보기 & 적용 컨펌 모달 */}
      {showPreviewModal && proposedData && (
        <Modal
          show={showPreviewModal}
          onHide={() => setShowPreviewModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="fs-6 fw-bold text-dark d-flex align-items-center gap-2">
              🤖 AI 제안 설계 검토 및 반영
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="alert alert-indigo small mb-3 py-2 px-3" style={{ backgroundColor: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe', fontSize: '0.78rem' }}>
              <strong>안내:</strong> AI가 생성한 데이터베이스 설계 제안입니다. 아래 내용을 검토하신 후 현재 ERD 캔버스에 추가로 병합할지, 또는 기존 설계를 모두 지우고 완전히 대체할지 선택해 주세요.
            </div>

            <div className="proposed-tables-preview mb-3" style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '4px' }}>
              {(proposedData.tables || []).map((t, tidx) => (
                <div key={tidx} className="card mb-3 shadow-sm border-light-subtle">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3" style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <strong style={{ color: '#4f46e5', fontSize: '0.82rem' }}>{t.physicalName}</strong>
                    <span className="text-secondary small fw-bold" style={{ fontSize: '0.75rem' }}>{t.logicalName || '(논리명 없음)'}</span>
                  </div>
                  <div className="card-body p-3 bg-white">
                    {t.comment && <p className="text-muted small mb-3" style={{ fontSize: '0.74rem', borderLeft: '3px solid #6366f1', paddingLeft: '8px' }}>{t.comment}</p>}
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.72rem' }}>
                      <thead>
                        <tr className="bg-light text-secondary">
                          <th>컬럼 물리명</th>
                          <th>컬럼 논리명</th>
                          <th>데이터 타입</th>
                          <th className="text-center">PK</th>
                          <th className="text-center">FK</th>
                          <th className="text-center">Not Null</th>
                          <th>기본값</th>
                          <th>설명</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(t.columns || []).map((c, cidx) => (
                          <tr key={cidx}>
                            <td><strong>{c.physicalName}</strong></td>
                            <td className="text-secondary">{c.logicalName || '-'}</td>
                            <td>{c.dataType}{c.size && c.size.toString().replace(/['"]/g, '').trim() ? `(${c.size.toString().replace(/['"]/g, '').trim()})` : ''}</td>
                            <td className="text-center">{c.pk ? '✅' : ''}</td>
                            <td className="text-center">{c.fk ? '🔗' : ''}</td>
                            <td className="text-center">{c.notNull ? '✔️' : ''}</td>
                            <td><code style={{ fontSize: '0.65rem', color: '#b91c1c' }}>{c.defaultValue || '-'}</code></td>
                            <td className="text-secondary" style={{ fontSize: '0.68rem' }}>{c.comment || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {proposedData.relationships && proposedData.relationships.length > 0 && (
                <div className="card shadow-sm border-light-subtle">
                  <div className="card-header bg-light py-2 px-3" style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <strong style={{ fontSize: '0.8rem', color: '#1e293b' }}>🔗 관계선 정의 ({proposedData.relationships.length}개)</strong>
                  </div>
                  <div className="card-body p-2 bg-white">
                    <ul className="list-group list-group-flush mb-0" style={{ fontSize: '0.72rem' }}>
                      {proposedData.relationships.map((r, ridx) => (
                        <li key={ridx} className="list-group-item d-flex justify-content-between align-items-center py-2 px-3 border-0 border-bottom">
                          <span className="fw-semibold">{r.parentTable}.{r.parentColumn}</span>
                          <span className="badge bg-indigo-subtle text-indigo rounded-pill px-2 py-1" style={{ fontSize: '0.68rem', backgroundColor: '#e0e7ff', color: '#4f46e5' }}>{r.type || '1:N'} 관계</span>
                          <span className="fw-semibold text-slate-700">{r.childTable}.{r.childColumn}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={() => setShowPreviewModal(false)}
              className="small py-2 px-3"
            >
              닫기
            </Button>
            <div className="d-flex gap-2">
              <Button
                variant="outline-indigo"
                onClick={() => handleApplyProposedData('ADD')}
                className="small py-2 px-3"
                style={{ borderColor: '#6366f1', color: '#6366f1' }}
              >
                기존 설계에 추가 (ADD)
              </Button>
              <Button
                variant="indigo"
                onClick={() => handleApplyProposedData('REPLACE')}
                className="small py-2 px-3 text-white"
                style={{ backgroundColor: '#6366f1', border: 'none' }}
              >
                기존 설계 대체 (REPLACE)
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
